import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Users, Package, Tag, ShoppingCart, Star, Database, LayoutDashboard,
  Plus, Pencil, Trash2, Search, ChevronDown, X, Check,
  AlertCircle, ArrowUpRight, ArrowDownRight, Activity, Layers, Menu, RefreshCw,
  Upload, ImageIcon, Loader2, TrendingUp, DollarSign, Clock, Truck, UserCheck,
  FileSpreadsheet, Download, CheckCircle2, FileText, Eye, Phone, MapPin,
  Mail, Send, Megaphone
} from "lucide-react";
import Papa from "papaparse";

// ─── CONFIG ───────────────────────────────────────────────────────────────────
const API = import.meta.env.VITE_API_URL || "https://daroulmesbackend.onrender.com";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const C = {
  forest: "#1f3d3a",
  gold: "#dfcc25",
  sand: "#c8a98a",
  cream: "#f3eee9",
  tan: "#6b5744",
};

const CHART_COLORS = ["#1f3d3a", "#dfcc25", "#c8a98a", "#059669", "#7c3aed", "#0369a1", "#d97706", "#db2777"];

// ─── API HELPERS ──────────────────────────────────────────────────────────────
const getToken = () => localStorage.getItem("access_token") || "";

async function apiFetch(path, opts = {}) {
  const headers = { Authorization: `Bearer ${getToken()}`, ...opts.headers };
  if (!(opts.body instanceof FormData)) headers["Content-Type"] = "application/json";
  const res = await fetch(`${API}${path}`, { ...opts, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || err.message || res.statusText);
  }
  if (res.status === 204) return null;
  return res.json();
}

const api = {
  login: (body) => apiFetch("/api/auth/login", { method: "POST", body: JSON.stringify(body) }),
  register: (body) => apiFetch("/api/auth/register", { method: "POST", body: JSON.stringify(body) }),
  getUsers: () => apiFetch("/api/users"),
  getUser: (id) => apiFetch(`/api/users/${id}`),
  updateUser: (id, body) => apiFetch(`/api/users/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteUser: (id) => apiFetch(`/api/users/${id}`, { method: "DELETE" }),
  getCategories: () => apiFetch("/api/categories"),
  createCategory: (body) => apiFetch("/api/categories", { method: "POST", body: JSON.stringify(body) }),
  updateCategory: (id, body) => apiFetch(`/api/categories/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteCategory: (id) => apiFetch(`/api/categories/${id}`, { method: "DELETE" }),
  getProducts: (params = "") => apiFetch(`/api/products?limit=100${params}`),
  getProduct: (id) => apiFetch(`/api/products/${id}?includeMetadata=true&includeReviews=true`),
  createProduct: (formData) => apiFetch("/api/products", { method: "POST", body: formData }),
  bulkCreateProducts: (products) => apiFetch("/api/products/bulk", { method: "POST", body: JSON.stringify({ products }) }),
  updateProduct: (id, formData) => apiFetch(`/api/products/${id}`, { method: "PUT", body: formData }),
  deleteProduct: (id) => apiFetch(`/api/products/${id}`, { method: "DELETE" }),
  getMetadata: (productId) => apiFetch(`/api/products/${productId}/metadata`),
  createMetadata: (body) => apiFetch(`/api/products/${body.productId}/metadata`, { method: "POST", body: JSON.stringify(body) }),
  updateMetadata: (id, body) => apiFetch(`/api/metadata/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteMetadata: (id) => apiFetch(`/api/metadata/${id}`, { method: "DELETE" }),
  replaceMetadata: (productId, body) => apiFetch(`/api/products/${productId}/metadata`, { method: "PUT", body: JSON.stringify(body) }),
  getOrders: () => apiFetch("/api/orders?limit=100"),
  getOrder: (id) => apiFetch(`/api/orders/${id}`),
  createOrder: (body) => apiFetch("/api/orders", { method: "POST", body: JSON.stringify(body) }),
  updateOrder: (id, body) => apiFetch(`/api/orders/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteOrder: (id) => apiFetch(`/api/orders/${id}`, { method: "DELETE" }),
  getReviews: (productId) => apiFetch(`/api/products/${productId}/reviews`),
  getAllReviews: () => apiFetch("/api/reviews"),
  deleteReview: (id) => apiFetch(`/api/reviews/${id}`, { method: "DELETE" }),
  updateReview: (id, body) => apiFetch(`/api/reviews/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  getSessions: () => apiFetch("/api/sessions"),
  deleteExpiredSessions: () => apiFetch("/api/sessions/expired", { method: "DELETE" }),
  deleteSession: (id) => apiFetch(`/api/sessions/${id}`, { method: "DELETE" }),
  getCampaignRecipients: (audience = "USERS") => apiFetch(`/api/campaigns/recipients?audience=${encodeURIComponent(audience)}`),
  sendCampaign: (body) => apiFetch("/api/campaigns/send", { method: "POST", body: JSON.stringify(body) }),
  // !NEW — delivery endpoints
  getDeliveryUsers: () => apiFetch("/api/users?role=DELIVERY"),
  assignDelivery: (orderId, deliveryUserId, deliveryNote) =>
    apiFetch(`/api/orders/${orderId}/assign-delivery`, {
      method: "PUT",
      body: JSON.stringify({ deliveryUserId, deliveryNote }),
    }),
  updateDeliveryStatus: (orderId, deliveryStatus, extra = {}) =>
    apiFetch(`/api/orders/${orderId}/delivery-status`, {
      method: "PUT",
      body: JSON.stringify({ deliveryStatus, ...extra }),
    }),
};

// ─── FAST PRODUCT CREATION HELPERS ───────────────────────────────────────────
// Large phone photos make the product request feel slow because they must upload
// to the backend and then to Cloudinary. This resizes/compresses big images in
// the browser before sending them. Small images and GIFs are kept untouched.
const MAX_PRODUCT_IMAGE_SIDE = 1400;
const PRODUCT_IMAGE_QUALITY = 0.78;

async function optimizeProductImage(file) {
  if (!file || !file.type?.startsWith("image/") || file.type === "image/gif") return file;
  if (file.size <= 850 * 1024) return file;

  const loadImage = () => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not optimize image"));
    };
    img.src = url;
  });

  try {
    const img = await loadImage();
    const scale = Math.min(1, MAX_PRODUCT_IMAGE_SIDE / Math.max(img.width, img.height));
    const width = Math.max(1, Math.round(img.width * scale));
    const height = Math.max(1, Math.round(img.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { alpha: false });
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/webp", PRODUCT_IMAGE_QUALITY));
    if (!blob || blob.size >= file.size) return file;

    const baseName = file.name.replace(/.[^.]+$/, "") || "product-image";
    return new File([blob], baseName + ".webp", { type: "image/webp", lastModified: Date.now() });
  } catch {
    return file;
  }
}

function mergeProductResponseIntoCache(prev, product, previewUrl) {
  const hydrated = { ...product, imageUrl: previewUrl || product.imageUrl || null };
  if (!prev) return { data: [hydrated] };
  if (Array.isArray(prev)) {
    const exists = prev.some(p => p.id === hydrated.id);
    return exists ? prev.map(p => (p.id === hydrated.id ? { ...p, ...hydrated } : p)) : [hydrated, ...prev];
  }
  const list = Array.isArray(prev.data) ? prev.data : [];
  const exists = list.some(p => p.id === hydrated.id);
  const nextData = exists ? list.map(p => (p.id === hydrated.id ? { ...p, ...hydrated } : p)) : [hydrated, ...list];
  return {
    ...prev,
    data: nextData,
    pagination: prev.pagination ? { ...prev.pagination, total: (prev.pagination.total || list.length) + (exists ? 0 : 1) } : prev.pagination,
  };
}

function replaceProductInCache(prev, product, previewUrl) {
  const hydrated = { ...product, imageUrl: previewUrl || product.imageUrl || null };
  if (!prev) return prev;
  if (Array.isArray(prev)) return prev.map(p => (p.id === hydrated.id ? { ...p, ...hydrated } : p));
  const list = Array.isArray(prev.data) ? prev.data : [];
  return { ...prev, data: list.map(p => (p.id === hydrated.id ? { ...p, ...hydrated } : p)) };
}

// ─── HOOK ─────────────────────────────────────────────────────────────────────
function useResource(fetchFn, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await fetchFn()); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, deps);
  useEffect(() => { load(); }, [load]);
  return { data, loading, error, reload: load, setData };
}

// ─── PURE SVG CHARTS ─────────────────────────────────────────────────────────

function BarChart({ data = [], height = 180, color = C.forest, valueKey = "value", labelKey = "label" }) {
  if (!data.length) return <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center", color: "#ccc", fontSize: 12 }}>No data</div>;
  const W = 400;
  const LABEL_H = 20;
  const chartH = height - LABEL_H;
  const pad = 6;
  const slotW = W / data.length;
  const barW = Math.min(slotW * 0.55, 36);
  const max = Math.max(...data.map(d => d[valueKey] || 0)) || 1;
  return (
    <div style={{ width: "100%" }}>
      <svg viewBox={`0 0 ${W} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height }}>
        {[0.25, 0.5, 0.75, 1].map(f => (
          <line key={f} x1={0} y1={(1 - f) * (chartH - pad)} x2={W} y2={(1 - f) * (chartH - pad)} stroke="rgba(31,61,58,0.06)" strokeWidth="1" />
        ))}
        {data.map((d, i) => {
          const barH = Math.max(((d[valueKey] || 0) / max) * (chartH - pad * 2), d[valueKey] ? 4 : 0);
          const cx = slotW * i + slotW / 2;
          return (
            <g key={i}>
              <rect x={cx - barW / 2} y={chartH - barH - pad} width={barW} height={barH} fill={color} rx="5" opacity="0.88" />
              <text x={cx} y={height - 4} textAnchor="middle" fontSize="9.5" fill={C.tan}
                style={{ overflow: "hidden" }}>
                {String(d[labelKey]).slice(0, 8)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function LineAreaChart({ data = [], height = 160, color = C.forest, valueKey = "value", labelKey = "label", uid = "grad" }) {
  if (data.length < 2) return <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center", color: "#ccc", fontSize: 12 }}>Not enough data</div>;
  const W = 600, LABEL_H = 24, H = height - LABEL_H, pad = 12;
  const vals = data.map(d => d[valueKey] || 0);
  const max = Math.max(...vals) || 1, min = Math.min(...vals);
  const pts = data.map((d, i) => [
    pad + (i / (data.length - 1)) * (W - pad * 2),
    pad + (1 - (d[valueKey] - min) / (max - min || 1)) * (H - pad * 2),
  ]);
  const smooth = pts.map((p, i) => {
    if (i === 0) return `M${p[0]},${p[1]}`;
    const prev = pts[i - 1];
    return `C${prev[0] + 40},${prev[1]} ${p[0] - 40},${p[1]} ${p[0]},${p[1]}`;
  }).join(" ");
  const area = `${smooth} L${pts[pts.length-1][0]},${H} L${pts[0][0]},${H} Z`;
  return (
    <div style={{ width: "100%" }}>
      <svg viewBox={`0 0 ${W} ${height}`} style={{ width: "100%", height }} preserveAspectRatio="none">
        <defs>
          <linearGradient id={uid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0.01" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map(f => (
          <line key={f} x1={pad} y1={pad + f*(H-pad*2)} x2={W-pad} y2={pad + f*(H-pad*2)} stroke="rgba(31,61,58,0.06)" strokeWidth="1" />
        ))}
        <path d={area} fill={`url(#${uid})`} />
        <path d={smooth} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
        {pts.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="4" fill="#fff" stroke={color} strokeWidth="2.2" />
        ))}
        {data.map((d, i) => (
          <text key={i} x={pts[i][0]} y={height - 5} textAnchor="middle" fontSize="9.5" fill={C.tan}>{d[labelKey]}</text>
        ))}
      </svg>
    </div>
  );
}

function DonutChart({ segments = [], size = 150, thickness = 30, centerLabel, centerSub }) {
  const total = segments.reduce((s, sg) => s + (sg.value || 0), 0) || 1;
  const R = size / 2 - thickness / 2 - 2;
  const cx = size / 2, cy = size / 2;
  let cum = 0;
  const arcs = segments.map(sg => {
    const frac = (sg.value || 0) / total;
    const startAngle = cum * 2 * Math.PI - Math.PI / 2;
    cum += frac;
    const endAngle = cum * 2 * Math.PI - Math.PI / 2;
    // Clamp to avoid degenerate arcs
    if (frac <= 0) return { ...sg, d: null, frac };
    if (frac >= 1) {
      // Full circle: draw two half-arcs
      const top = { x: cx + R * Math.cos(-Math.PI / 2), y: cy + R * Math.sin(-Math.PI / 2) };
      const bot = { x: cx + R * Math.cos(Math.PI / 2), y: cy + R * Math.sin(Math.PI / 2) };
      return { ...sg, d: `M${top.x} ${top.y} A${R} ${R} 0 1 1 ${bot.x} ${bot.y} A${R} ${R} 0 1 1 ${top.x} ${top.y}`, frac };
    }
    const x1 = cx + R * Math.cos(startAngle), y1 = cy + R * Math.sin(startAngle);
    const x2 = cx + R * Math.cos(endAngle), y2 = cy + R * Math.sin(endAngle);
    return { ...sg, d: `M${x1} ${y1} A${R} ${R} 0 ${frac > 0.5 ? 1 : 0} 1 ${x2} ${y2}`, frac };
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={R} fill="none" stroke="rgba(31,61,58,0.07)" strokeWidth={thickness} />
      {arcs.map((arc, i) => arc.d && (
        <path key={i} d={arc.d} fill="none" stroke={arc.color} strokeWidth={thickness} strokeLinecap="round" />
      ))}
      {centerLabel && <>
        <text x={cx} y={cy - 5} textAnchor="middle" fontSize="17" fontWeight="800" fill={C.forest} fontFamily="'Poiret One', cursive">{centerLabel}</text>
        {centerSub && <text x={cx} y={cy + 13} textAnchor="middle" fontSize="9.5" fill={C.tan}>{centerSub}</text>}
      </>}
    </svg>
  );
}

function ProgressBar({ label, value, max, color, count }) {
  const pct = max ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ marginBottom: 11 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
          <span style={{ fontSize: 12, color: C.forest, fontWeight: 600 }}>{label}</span>
        </div>
        <span style={{ fontSize: 11, color: C.tan }}>{count} <span style={{ color: "#bbb" }}>({pct}%)</span></span>
      </div>
      <div style={{ height: 5, background: "rgba(31,61,58,0.07)", borderRadius: 99 }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 99, transition: "width 1s ease" }} />
      </div>
    </div>
  );
}

// ─── SHARED UI ────────────────────────────────────────────────────────────────

function Badge({ children, variant = "default" }) {
  const styles = {
    default: { background: "rgba(31,61,58,0.1)", color: C.forest },
    success: { background: "rgba(34,197,94,0.12)", color: "#15803d" },
    danger:  { background: "rgba(239,68,68,0.1)", color: "#b91c1c" },
    gold:    { background: C.gold + "30", color: "#a08c0a" },
    sand:    { background: C.sand + "25", color: C.tan },
    admin:   { background: C.forest, color: C.gold },
  };
  return <span style={{ ...styles[variant], padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", display: "inline-block" }}>{children}</span>;
}

function StatCard({ icon: Icon, label, value, sub, accent, trend }) {
  const isUp = trend >= 0;
  return (
    <div style={{ background: "#fff", borderRadius: 20, padding: "20px 22px", display: "flex", flexDirection: "column", gap: 8, boxShadow: "0 2px 16px rgba(31,61,58,0.07)", border: "1px solid rgba(31,61,58,0.07)", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -22, right: -22, width: 90, height: 90, borderRadius: "50%", background: accent + "10" }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ width: 42, height: 42, borderRadius: 13, background: accent + "16", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={20} color={accent} />
        </div>
        {trend !== undefined && (
          <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 700, color: isUp ? "#15803d" : "#b91c1c", background: isUp ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.08)", padding: "3px 8px", borderRadius: 99 }}>
            {isUp ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}{Math.abs(trend)}%
          </div>
        )}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: C.forest, fontFamily: "'Poiret One', cursive", lineHeight: 1.1 }}>{value ?? "—"}</div>
      <div style={{ fontSize: 12, color: C.tan, fontWeight: 600 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: "#aaa", display: "flex", alignItems: "center", gap: 4 }}><Activity size={10} />{sub}</div>}
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div style={{ background: "#fff", borderRadius: 20, padding: "22px 24px", boxShadow: "0 2px 16px rgba(31,61,58,0.07)", border: "1px solid rgba(31,61,58,0.07)" }}>
      <div style={{ marginBottom: 18 }}>
        <h4 style={{ fontFamily: "'Poiret One', cursive", fontSize: 15, color: C.forest, margin: 0 }}>{title}</h4>
        {subtitle && <p style={{ fontSize: 11, color: "#aaa", margin: "3px 0 0" }}>{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function Inp({ value, onChange, placeholder, style, type = "text" }) {
  return <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
    style={{ border: "1.5px solid rgba(31,61,58,0.15)", borderRadius: 10, padding: "8px 12px", fontSize: 13, outline: "none", background: "#fff", color: C.forest, fontFamily: "'Poiret One', cursive", width: "100%", ...style }} />;
}

function Sel({ value, onChange, options, style }) {
  return <select value={value} onChange={e => onChange(e.target.value)}
    style={{ border: "1.5px solid rgba(31,61,58,0.15)", borderRadius: 10, padding: "8px 12px", fontSize: 13, outline: "none", background: "#fff", color: C.forest, fontFamily: "'Poiret One', cursive", cursor: "pointer", width: "100%", ...style }}>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>;
}

function Btn({ children, onClick, variant = "primary", size = "md", style, disabled }) {
  const base = { display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 10, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", border: "none", fontFamily: "'Poiret One', cursive", transition: "all 0.18s", fontSize: size === "sm" ? 12 : 13, padding: size === "sm" ? "5px 12px" : "8px 16px", opacity: disabled ? 0.6 : 1 };
  const variants = { primary: { background: C.forest, color: C.gold }, danger: { background: "#fee2e2", color: "#b91c1c" }, ghost: { background: "rgba(31,61,58,0.06)", color: C.forest }, gold: { background: C.gold, color: C.forest } };
  return <button onClick={onClick} disabled={disabled} style={{ ...base, ...variants[variant], ...style }}>{children}</button>;
}

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.35)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 20, width: "min(560px,95vw)", maxHeight: "85vh", overflow: "auto", boxShadow: "0 30px 80px rgba(0,0,0,0.2)", padding: "28px 28px 24px" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ fontFamily: "'Poiret One', cursive", fontSize: 22, color: C.forest, margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.tan }}><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ConfirmModal({ open, onClose, onConfirm, message, loading }) {
  return (
    <Modal open={open} onClose={onClose} title="Confirm Delete">
      <p style={{ color: C.tan, marginBottom: 24, fontSize: 14 }}>{message || "Are you sure you want to delete this item? This action cannot be undone."}</p>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant="danger" onClick={onConfirm} disabled={loading}>{loading ? <Loader2 size={14} /> : <Trash2 size={14} />}Delete</Btn>
      </div>
    </Modal>
  );
}

function Table({ columns, data, emptyMsg }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: C.cream }}>
            {columns.map(col => <th key={col.key} style={{ textAlign: "left", padding: "10px 14px", color: C.tan, fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.07em", whiteSpace: "nowrap" }}>{col.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {!data || data.length === 0
            ? <tr><td colSpan={columns.length} style={{ textAlign: "center", padding: 40, color: "#bbb" }}><AlertCircle size={24} style={{ margin: "0 auto 8px", display: "block" }} />{emptyMsg || "No data found."}</td></tr>
            : data.map((row, ri) => (
              <tr key={row.id ?? ri} style={{ borderBottom: "1px solid rgba(31,61,58,0.06)", transition: "background 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.background = C.cream + "80"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                {columns.map(col => (
                  <td key={col.key} style={{ padding: "11px 14px", color: C.forest, verticalAlign: "middle" }}>
                    {col.render ? col.render(row[col.key], row) : (row[col.key] ?? <span style={{ color: "#ccc" }}>—</span>)}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

function FF({ label, children }) {
  return <div style={{ marginBottom: 14 }}>
    <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: C.tan, marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</label>
    {children}
  </div>;
}

function Toast({ msg, type }) {
  if (!msg) return null;
  const colors = { success: "#15803d", error: "#b91c1c" };
  return <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 2000, background: "#fff", border: `2px solid ${colors[type] || C.forest}`, borderRadius: 14, padding: "12px 20px", boxShadow: "0 8px 30px rgba(0,0,0,0.12)", color: colors[type] || C.forest, fontWeight: 700, fontSize: 13, maxWidth: 320 }}>{msg}</div>;
}

function Loading() {
  return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 60, gap: 10, color: C.tan }}><Loader2 size={20} style={{ animation: "spin 1s linear infinite" }} />Loading…</div>;
}

function SectionError({ error, reload }) {
  return <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: 60, color: "#b91c1c" }}>
    <AlertCircle size={28} /><div style={{ fontSize: 14 }}>{error}</div>
    <Btn variant="ghost" onClick={reload}><RefreshCw size={14} />Retry</Btn>
  </div>;
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard() {
  const products  = useResource(() => api.getProducts());
  const categories= useResource(() => api.getCategories());
  const orders    = useResource(() => api.getOrders());
  const reviews   = useResource(() => api.getAllReviews());
  const sessions  = useResource(() => api.getSessions());
  const users     = useResource(() => api.getUsers());

  const prods = products.data?.data  || products.data  || [];
  const cats  = categories.data      || [];
  const ords  = orders.data?.data    || orders.data    || [];
  const revs  = Array.isArray(reviews.data)  ? reviews.data  : reviews.data?.data  || [];
  const sess  = Array.isArray(sessions.data) ? sessions.data : sessions.data?.data || [];
  const usrs  = users.data || [];

  const confirmedOrders = ords.filter(o => o.isConfirmed);
  const revenue         = confirmedOrders.reduce((s, o) => s + (o.totalAmount || 0), 0);
  const avgRating       = revs.length ? revs.reduce((s, r) => s + r.rating, 0) / revs.length : 0;
  const activeSess      = sess.filter(s => new Date(s.expiresAt) > new Date());
  const expiredSess     = sess.filter(s => new Date(s.expiresAt) <= new Date());
  const confirmedSess   = sess.filter(s => s.confirmedAt);

  // chart: orders by day
  const ordersByDay = (() => {
    const map = {};
    [...ords].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)).forEach(o => {
      const d = (o.createdAt || "").slice(5, 10);
      if (!map[d]) map[d] = { label: d, value: 0, revenue: 0 };
      map[d].value++;
      if (o.isConfirmed) map[d].revenue += o.totalAmount || 0;
    });
    const arr = Object.values(map).slice(-9);
    // Only return null if there are truly no orders at all
    return arr.length >= 1 ? arr : null;
  })();

  const catDist = cats.map((cat, i) => ({
    label: cat.name,
    value: prods.filter(p => p.categoryId === cat.id).length,
    color: CHART_COLORS[i % CHART_COLORS.length],
  }));

  const orderStatusSegs = [
    { label: "Confirmed", value: confirmedOrders.length, color: "#15803d" },
    { label: "Pending",   value: ords.filter(o => !o.isConfirmed).length, color: C.gold },
  ].filter(s => s.value > 0);

  const sessSegs = [
    { label: "Active",    value: activeSess.length,    color: C.forest },
    { label: "Expired",   value: expiredSess.length,   color: "#d97706" },
    { label: "Confirmed", value: confirmedSess.length, color: "#059669" },
  ].filter(s => s.value > 0);

  const ratingDist = [5,4,3,2,1].map(r => ({ label: `${r}★`, value: revs.filter(rv => Math.round(rv.rating) === r).length }));
  const topProds = [...prods].sort((a,b) => b.rating - a.rating).slice(0, 5);

  const stats = [
    { icon: Users,        label: "Total Users",    value: usrs.length,              sub: `${usrs.filter(u=>u.role==="ADMIN").length} admins`,  accent: C.forest,  trend: 8  },
    { icon: Package,      label: "Products",       value: prods.length,             sub: `${cats.length} categories`,                          accent: "#7c3aed", trend: 3  },
    { icon: ShoppingCart, label: "Orders",         value: ords.length,              sub: `${confirmedOrders.length} confirmed`,                 accent: "#0369a1", trend: 12 },
    { icon: DollarSign,   label: "Revenue (MAD)",  value: revenue.toLocaleString(), sub: "from confirmed orders",                              accent: "#059669", trend: 5  },
    { icon: Star,         label: "Avg Rating",     value: avgRating.toFixed(1),     sub: `${revs.length} reviews`,                             accent: "#d97706", trend: -2 },
    { icon: Database,     label: "Sessions",       value: sess.length,              sub: `${activeSess.length} active`,                        accent: C.sand,    trend: 1  },
  ];

  const anyLoading = products.loading || categories.loading || orders.loading || reviews.loading || sessions.loading || users.loading;
  const anyError = products.error || categories.error || orders.error;

  if (anyLoading) return <Loading />;
  if (anyError) return <SectionError error={anyError} reload={() => { products.reload(); categories.reload(); orders.reload(); }} />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Stat Row */}
      <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(165px,1fr))", gap: 14 }}>
        {stats.map(s => <StatCard key={s.label} {...s} />)}
      </div>

      {/* Revenue Banner + Line Chart */}
      <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16 }}>
        <div style={{ background: `linear-gradient(140deg, ${C.forest} 0%, #2d5a54 100%)`, borderRadius: 20, padding: "26px 24px", color: "#fff", position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ position: "absolute", top: -40, right: -40, width: 150, height: 150, borderRadius: "50%", background: C.gold + "14" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 30, height: 30, borderRadius: 9, background: C.gold + "22", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <TrendingUp size={15} color={C.gold} />
            </div>
            <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em", color: "rgba(255,255,255,0.45)", fontWeight: 700 }}>Confirmed Revenue</span>
          </div>
          <div style={{ fontSize: 36, fontWeight: 800, fontFamily: "'Poiret One', cursive", color: C.gold, lineHeight: 1 }}>
            {revenue.toLocaleString()} <span style={{ fontSize: 14, color: C.sand }}>MAD</span>
          </div>
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>From {confirmedOrders.length} confirmed orders</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            {cats.map((cat, i) => (
              <div key={cat.id} style={{ background: "rgba(255,255,255,0.08)", borderRadius: 10, padding: "7px 12px" }}>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", letterSpacing: "0.06em", textTransform: "uppercase" }}>{cat.name}</div>
                <div style={{ fontSize: 17, fontWeight: 800, color: "#fff" }}>{prods.filter(p=>p.categoryId===cat.id).length}</div>
              </div>
            ))}
          </div>
        </div>

        <ChartCard title="Revenue Trend" subtitle="Daily confirmed revenue (MAD)">
          {ordersByDay
            ? ordersByDay.length >= 2
              ? <LineAreaChart data={ordersByDay.map(d => ({ label: d.label, value: d.revenue }))} height={195} color={C.forest} uid="rev-trend" />
              : <BarChart data={ordersByDay.map(d => ({ label: d.label, value: d.revenue }))} height={195} color={C.forest} />
            : <div style={{ height: 195, display: "flex", alignItems: "center", justifyContent: "center", color: "#ccc", fontSize: 12 }}>Place some orders to see the trend</div>
          }
        </ChartCard>
      </div>

      {/* 3-col: category bar + order donut + session donut */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
        <ChartCard title="Products by Category" subtitle="Count per category">
          <BarChart data={catDist} height={165} color={C.forest} />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
            {catDist.map((c, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: C.tan }}>
                <div style={{ width: 8, height: 8, borderRadius: 2, background: CHART_COLORS[i % CHART_COLORS.length] }} />
                {c.label} ({c.value})
              </div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Order Status" subtitle="Confirmed vs. pending">
          <div style={{ display: "flex", alignItems: "center", gap: 18, justifyContent: "center" }}>
            <DonutChart segments={orderStatusSegs} size={140} thickness={28} centerLabel={ords.length} centerSub="Orders" />
            <div style={{ flex: 1 }}>
              {orderStatusSegs.map(s => <ProgressBar key={s.label} label={s.label} value={s.value} max={ords.length} color={s.color} count={s.value} />)}
            </div>
          </div>
        </ChartCard>

        <ChartCard title="Sessions" subtitle="Active · Expired · Confirmed">
          <div style={{ display: "flex", alignItems: "center", gap: 18, justifyContent: "center" }}>
            <DonutChart segments={sessSegs} size={140} thickness={28} centerLabel={sess.length} centerSub="Total" />
            <div style={{ flex: 1 }}>
              {sessSegs.map(s => <ProgressBar key={s.label} label={s.label} value={s.value} max={sess.length} color={s.color} count={s.value} />)}
            </div>
          </div>
        </ChartCard>
      </div>

      {/* Latest Orders + Rating Distribution */}
      <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 16 }}>
        <ChartCard title="Latest Orders" subtitle="6 most recent transactions">
          {ords.length === 0
            ? <div style={{ textAlign: "center", color: "#ccc", padding: 28, fontSize: 13 }}>No orders yet</div>
            : ords.slice(-6).reverse().map(o => (
              <div key={o.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid rgba(31,61,58,0.05)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 11, background: o.isConfirmed ? "rgba(34,197,94,0.1)" : C.cream, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <ShoppingCart size={14} color={o.isConfirmed ? "#15803d" : C.tan} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: C.forest }}>Order #{o.id}</div>
                    <div style={{ fontSize: 10, color: "#aaa", display: "flex", alignItems: "center", gap: 3 }}><Clock size={9}/>{o.createdAt?.slice(0, 10)}</div>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: C.forest }}>{(o.totalAmount || 0).toLocaleString()} MAD</div>
                  <Badge variant={o.isConfirmed ? "success" : "sand"}>{o.isConfirmed ? "Confirmed" : "Pending"}</Badge>
                </div>
              </div>
            ))
          }
        </ChartCard>

        <ChartCard title="Review Ratings" subtitle={`${revs.length} total · avg ${avgRating.toFixed(1)}★`}>
          <div style={{ marginBottom: 14 }}>
            {ratingDist.map(r => <ProgressBar key={r.label} label={r.label} value={r.value} max={revs.length || 1} color={C.gold} count={r.value} />)}
          </div>
          <div style={{ background: C.cream, borderRadius: 14, padding: "14px", textAlign: "center" }}>
            <div style={{ fontSize: 30, fontWeight: 800, color: C.forest, fontFamily: "'Poiret One', cursive" }}>{avgRating.toFixed(1)}</div>
            <div style={{ fontSize: 16, color: C.gold, margin: "2px 0" }}>{"⭐".repeat(Math.round(avgRating))}</div>
            <div style={{ fontSize: 11, color: C.tan }}>Average Rating</div>
          </div>
        </ChartCard>
      </div>

      {/* Top Products + Users Overview */}
      {(topProds.length > 0 || usrs.length > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {topProds.length > 0 && (
            <ChartCard title="Top Rated Products" subtitle="Ranked by star rating">
              {topProds.map((p, i) => {
                const cat = cats.find(c => c.id === p.categoryId);
                const imgSrc = p.imageUrl?.startsWith("/uploads") ? `${API}${p.imageUrl}` : p.imageUrl;
                return (
                  <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", borderBottom: i < topProds.length - 1 ? "1px solid rgba(31,61,58,0.05)" : "none" }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: i === 0 ? C.gold : C.tan, minWidth: 20, textAlign: "center" }}>#{i+1}</div>
                    {imgSrc
                      ? <img src={imgSrc} alt="" style={{ width: 36, height: 36, borderRadius: 9, objectFit: "cover", flexShrink: 0 }} />
                      : <div style={{ width: 36, height: 36, background: C.cream, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><ImageIcon size={13} color={C.tan} /></div>
                    }
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 12, color: C.forest, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                      <div style={{ fontSize: 10, color: C.tan }}>{cat?.name}</div>
                      <div style={{ height: 3, background: "rgba(31,61,58,0.07)", borderRadius: 99, marginTop: 4 }}>
                        <div style={{ height: "100%", width: `${((p.rating||0)/5)*100}%`, background: C.gold, borderRadius: 99 }} />
                      </div>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: C.forest }}>{p.rating?.toFixed ? p.rating.toFixed(1) : p.rating}★</div>
                  </div>
                );
              })}
            </ChartCard>
          )}

          <ChartCard title="Users Overview" subtitle="Role breakdown">
            <div style={{ display: "flex", gap: 12, marginBottom: 18 }}>
              {[
                { label: "Total", value: usrs.length,                              color: C.forest },
                { label: "Admins", value: usrs.filter(u=>u.role==="ADMIN").length, color: "#7c3aed" },
                { label: "Members",value: usrs.filter(u=>u.role==="USER").length,  color: "#0369a1" },
              ].map(s => (
                <div key={s.label} style={{ flex: 1, background: C.cream, borderRadius: 14, padding: "12px 10px", textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: s.color, fontFamily: "'Poiret One', cursive" }}>{s.value}</div>
                  <div style={{ fontSize: 10, color: C.tan, marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
            <BarChart
              data={[
                { label: "Admins",  value: usrs.filter(u=>u.role==="ADMIN").length },
                { label: "Members", value: usrs.filter(u=>u.role==="USER").length  },
              ]}
              height={110} color="#7c3aed"
            />
          </ChartCard>
        </div>
      )}
    </div>
  );
}

// ─── USERS SECTION ────────────────────────────────────────────────────────────
const USER_ROLES = [
  { value: "USER",     label: "User"     },
  { value: "ADMIN",    label: "Admin"    },
  { value: "DELIVERY", label: "Delivery" },
];
const roleBadgeVariant = r => r === "ADMIN" ? "admin" : r === "DELIVERY" ? "gold" : "default";

function UsersSection({ toast }) {
  const { data: users, loading, error, reload } = useResource(() => api.getUsers());
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "USER" });

  const filtered = (users || []).filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const openAdd  = () => { setForm({ name:"", email:"", password:"", role:"USER" }); setModal({ mode:"add" }); };
  const openEdit = u  => { setForm({ name: u.name||"", email: u.email, password:"", role: u.role }); setModal({ mode:"edit", item:u }); };

  const save = async () => {
    setSaving(true);
    try {
      if (modal.mode === "add") {
        await api.register({ name: form.name, email: form.email, password: form.password, role: form.role });
        toast("User created", "success");
      } else {
        await api.updateUser(modal.item.id, { name: form.name, email: form.email, role: form.role });
        toast("User updated", "success");
      }
      setModal(null);
      reload();
    } catch(e) { toast(e.message, "error"); }
    finally { setSaving(false); }
  };

  const del = async () => { setSaving(true); try { await api.deleteUser(deleteId); toast("User deleted","success"); setDeleteId(null); reload(); } catch(e){ toast(e.message,"error"); } finally { setSaving(false); } };

  if (loading) return <Loading />;
  if (error)   return <SectionError error={error} reload={reload} />;
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div className="section-header" style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
        <div style={{ position:"relative" }}>
          <Search size={14} style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:C.tan }} />
          <Inp value={search} onChange={setSearch} placeholder="Search users…" style={{ paddingLeft:32, minWidth:220 }} />
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <Btn variant="ghost" onClick={reload}><RefreshCw size={14}/>Refresh</Btn>
          <Btn onClick={openAdd}><Plus size={14}/>Add User</Btn>
        </div>
      </div>
      <div style={{ background:"#fff", borderRadius:16, overflow:"hidden", border:"1px solid rgba(31,61,58,0.08)" }}>
        <Table columns={[
          { key:"id",        label:"#" },
          { key:"name",      label:"Name",   render: v => v||<span style={{color:"#ccc"}}>—</span> },
          { key:"email",     label:"Email"  },
          { key:"role",      label:"Role",   render: v => <Badge variant={roleBadgeVariant(v)}>{v}</Badge> },
          { key:"createdAt", label:"Joined", render: v => v?.slice(0,10) },
          { key:"actions",   label:"Actions",render: (_,row) => (
            <div style={{display:"flex",gap:6}}>
              <Btn size="sm" variant="ghost" onClick={()=>openEdit(row)}><Pencil size={12}/></Btn>
              <Btn size="sm" variant="danger" onClick={()=>setDeleteId(row.id)}><Trash2 size={12}/></Btn>
            </div>
          )},
        ]} data={filtered} emptyMsg="No users found." />
      </div>
      <Modal open={!!modal} onClose={()=>setModal(null)} title={modal?.mode==="add"?"Add User":"Edit User"}>
        <FF label="Name"><Inp value={form.name} onChange={v=>setForm(f=>({...f,name:v}))} placeholder="Full name"/></FF>
        <FF label="Email *"><Inp value={form.email} onChange={v=>setForm(f=>({...f,email:v}))} placeholder="email@example.com"/></FF>
        {modal?.mode==="add" && (
          <FF label="Password *"><Inp type="password" value={form.password} onChange={v=>setForm(f=>({...f,password:v}))} placeholder="Min. 6 characters"/></FF>
        )}
        <FF label="Role"><Sel value={form.role} onChange={v=>setForm(f=>({...f,role:v}))} options={USER_ROLES}/></FF>
        <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:8}}>
          <Btn variant="ghost" onClick={()=>setModal(null)}>Cancel</Btn>
          <Btn onClick={save} disabled={saving}>{saving?<Loader2 size={14}/>:<Check size={14}/>}{modal?.mode==="add"?"Create":"Save"}</Btn>
        </div>
      </Modal>
      <ConfirmModal open={!!deleteId} onClose={()=>setDeleteId(null)} onConfirm={del} loading={saving}/>
    </div>
  );
}

// ─── CATEGORIES SECTION ───────────────────────────────────────────────────────
function CategoriesSection({ toast }) {
  const { data: categories, loading, error, reload } = useResource(() => api.getCategories());
  const { data: allProducts } = useResource(() => api.getProducts());
  const [modal, setModal] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name:"" });
  const products = allProducts?.data || allProducts || [];
  const openAdd  = () => { setForm({name:""}); setModal({mode:"add"}); };
  const openEdit = c => { setForm({name:c.name}); setModal({mode:"edit",item:c}); };
  const save = async () => {
    if (!form.name) return; setSaving(true);
    try { if(modal.mode==="add"){ await api.createCategory({name:form.name}); toast("Category created","success"); } else { await api.updateCategory(modal.item.id,{name:form.name}); toast("Category updated","success"); } setModal(null); reload(); }
    catch(e){ toast(e.message,"error"); } finally { setSaving(false); }
  };
  const del = async () => { setSaving(true); try { await api.deleteCategory(deleteId); toast("Category deleted","success"); setDeleteId(null); reload(); } catch(e){ toast(e.message,"error"); } finally { setSaving(false); } };
  if (loading) return <Loading />;
  if (error)   return <SectionError error={error} reload={reload}/>;
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
        <Btn variant="ghost" onClick={reload}><RefreshCw size={14}/>Refresh</Btn>
        <Btn onClick={openAdd}><Plus size={14}/>Add Category</Btn>
      </div>
      <div className="categories-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:14 }}>
        {(categories||[]).map(cat => {
          const count = products.filter(p=>p.categoryId===cat.id).length;
          return (
            <div key={cat.id} style={{ background:"#fff", borderRadius:18, padding:"20px 22px", boxShadow:"0 2px 16px rgba(31,61,58,0.07)", border:"1px solid rgba(31,61,58,0.07)", display:"flex", flexDirection:"column", gap:10 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                <div style={{ width:44, height:44, borderRadius:14, background:C.forest, display:"flex", alignItems:"center", justifyContent:"center" }}><Tag size={20} color={C.gold}/></div>
                <div style={{ display:"flex", gap:6 }}>
                  <Btn size="sm" variant="ghost" onClick={()=>openEdit(cat)}><Pencil size={12}/></Btn>
                  <Btn size="sm" variant="danger" onClick={()=>setDeleteId(cat.id)}><Trash2 size={12}/></Btn>
                </div>
              </div>
              <div>
                <div style={{ fontWeight:800, fontSize:18, color:C.forest }}>{cat.name}</div>
                <div style={{ fontSize:12, color:C.tan }}>{count} products · since {cat.createdAt?.slice(0,10)}</div>
              </div>
            </div>
          );
        })}
      </div>
      <Modal open={!!modal} onClose={()=>setModal(null)} title={modal?.mode==="add"?"Add Category":"Edit Category"}>
        <FF label="Name *"><Inp value={form.name} onChange={v=>setForm(f=>({...f,name:v}))} placeholder="e.g. Kitchen"/></FF>
        <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:8}}>
          <Btn variant="ghost" onClick={()=>setModal(null)}>Cancel</Btn>
          <Btn onClick={save} disabled={saving}>{saving?<Loader2 size={14}/>:<Check size={14}/>}{modal?.mode==="add"?"Create":"Save"}</Btn>
        </div>
      </Modal>
      <ConfirmModal open={!!deleteId} onClose={()=>setDeleteId(null)} onConfirm={del} loading={saving}/>
    </div>
  );
}

// ─── PRODUCT CSV IMPORT ───────────────────────────────────────────────────────
const CSV_SCHEMA_FIELDS = [
  { key:"name",        label:"Name",        type:"string", required:true  },
  { key:"categoryId",  label:"Category ID", type:"int",    required:true  },
  { key:"price",       label:"Price",       type:"float",  required:true  },
  { key:"origin",      label:"Origin",      type:"string", required:false },
  { key:"tag",         label:"Tag",         type:"string", required:false },
  { key:"imageUrl",    label:"Image URL",   type:"string", required:false },
  { key:"description", label:"Description", type:"string", required:false },
  { key:"rating",      label:"Rating",      type:"float",  required:false },
];
const CSV_IGNORE = "__ignore__";

const csvCoerce = (raw, type) => {
  if (raw === undefined || raw === null) return null;
  const v = String(raw).trim();
  if (v === "") return null;
  if (type === "int")   { const n = parseInt(v, 10);   return Number.isFinite(n) ? n : NaN; }
  if (type === "float") { const n = parseFloat(v);     return Number.isFinite(n) ? n : NaN; }
  return v;
};

const csvGuessMapping = (header) => {
  const h = header.toLowerCase().replace(/[\s_-]/g, "");
  const aliases = {
    name:        ["name","title","productname"],
    categoryId:  ["categoryid","category","catid","cat"],
    price:       ["price","cost","amount"],
    origin:      ["origin","orig","country","madein"],
    tag:         ["tag","tags","label","badge"],
    imageUrl:    ["imageurl","img","image","picture","photo","thumbnail"],
    description: ["description","desc","details","about"],
    rating:      ["rating","rate","stars","score"],
  };
  for (const [field, list] of Object.entries(aliases)) {
    if (list.some(a => a === h)) return field;
  }
  return CSV_IGNORE;
};

const csvBuildProduct = (row, mapping) => {
  const out = {};
  const errors = [];
  for (const field of CSV_SCHEMA_FIELDS) {
    const sourceCol = Object.entries(mapping).find(([, m]) => m === field.key)?.[0];
    if (!sourceCol) { if (field.required) errors.push(`${field.label} not mapped`); continue; }
    const val = csvCoerce(row[sourceCol], field.type);
    if (val === null)          { if (field.required) errors.push(`${field.label} is empty`); continue; }
    if (Number.isNaN(val))     { errors.push(`${field.label} is not a valid ${field.type}`); continue; }
    out[field.key] = val;
  }
  return { product: out, errors };
};

function ProductCsvImport({ open, onClose, onSuccess, toast, categories }) {
  const [file, setFile]             = useState(null);
  const [rows, setRows]             = useState([]);
  const [headers, setHeaders]       = useState([]);
  const [mapping, setMapping]       = useState({});
  const [parsing, setParsing]       = useState(false);
  const [importing, setImporting]   = useState(false);
  const [results, setResults]       = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const validCatIds = new Set((categories || []).map(c => c.id));

  const preview = (() => {
    if (!rows.length) return { valid: [], invalid: [] };
    const valid = [], invalid = [];
    rows.forEach((row, idx) => {
      const { product, errors } = csvBuildProduct(row, mapping);
      if (product.categoryId !== undefined && !validCatIds.has(product.categoryId)) {
        errors.push(`Category ${product.categoryId} does not exist`);
      }
      if (errors.length) invalid.push({ idx, product, errors });
      else               valid.push({ idx, product });
    });
    return { valid, invalid };
  })();

  const unmappedRequired = CSV_SCHEMA_FIELDS.filter(
    f => f.required && !Object.values(mapping).includes(f.key)
  );

  const reset = () => {
    setFile(null); setRows([]); setHeaders([]); setMapping({});
    setResults(null); if (inputRef.current) inputRef.current.value = "";
  };

  const handleClose = () => { reset(); onClose(); };

  const handleFile = (f) => {
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) { toast("Please upload a .csv file", "error"); return; }
    setFile(f); setParsing(true); setResults(null);
    Papa.parse(f, {
      header: true, skipEmptyLines: true,
      complete: (res) => {
        const hdrs = res.meta.fields || [];
        setHeaders(hdrs); setRows(res.data);
        const auto = {};
        hdrs.forEach(h => { auto[h] = csvGuessMapping(h); });
        setMapping(auto); setParsing(false);
      },
      error: (err) => { toast(`Parse error: ${err.message}`, "error"); setParsing(false); }
    });
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragActive(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const downloadTemplate = () => {
    const csv = [
      CSV_SCHEMA_FIELDS.map(f => f.key).join(","),
      `"Sample Product",1,29.99,"Morocco","featured","https://example.com/img.jpg","Nice product",4.5`
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "products-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const runImport = async () => {
    if (!preview.valid.length) return;
    setImporting(true);
    try {
      const payload = preview.valid.map(v => v.product);
      const res = await api.bulkCreateProducts(payload);
      setResults({
        created: res.created || 0,
        skipped: res.skipped || [],
        total: preview.valid.length,
      });
      toast(`Imported ${res.created || 0} products`, "success");
      onSuccess?.();
    } catch (e) {
      toast(e.message || "Bulk import failed", "error");
      setResults({ created: 0, skipped: [{ error: e.message }], total: preview.valid.length });
    } finally {
      setImporting(false);
    }
  };

  if (!open) return null;

  return (
    <div style={{ position:"fixed", inset:0, zIndex:1000, background:"rgba(0,0,0,0.35)", backdropFilter:"blur(4px)", display:"flex", alignItems:"center", justifyContent:"center" }} onClick={handleClose}>
      <div style={{ background:"#fff", borderRadius:20, width:"min(900px,95vw)", maxHeight:"90vh", overflow:"auto", boxShadow:"0 30px 80px rgba(0,0,0,0.2)", padding:"28px 28px 24px" }} onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:38, height:38, background:C.cream, borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <FileSpreadsheet size={18} color={C.forest}/>
            </div>
            <div>
              <h3 style={{ fontFamily:"'Poiret One', cursive", fontSize:22, color:C.forest, margin:0 }}>Import Products from CSV</h3>
              <p style={{ fontSize:12, color:C.tan, margin:"2px 0 0" }}>Bulk upload — auto-maps columns to your schema.</p>
            </div>
          </div>
          <button onClick={handleClose} style={{ background:"none", border:"none", cursor:"pointer", color:C.tan }}><X size={20}/></button>
        </div>

        {/* STEP 1: UPLOAD */}
        {!file && !results && (
          <div>
            <div
              onDragOver={e=>{e.preventDefault();setDragActive(true);}}
              onDragLeave={()=>setDragActive(false)}
              onDrop={onDrop}
              onClick={()=>inputRef.current?.click()}
              style={{ border:`2px dashed ${dragActive?C.forest:"rgba(31,61,58,0.2)"}`, borderRadius:16, padding:"56px 20px", textAlign:"center", cursor:"pointer", background:dragActive?C.cream:"#fafaf8", transition:"all 0.2s" }}>
              <input ref={inputRef} type="file" accept=".csv" style={{display:"none"}} onChange={e=>handleFile(e.target.files?.[0])}/>
              <Upload size={42} color={C.tan} style={{ margin:"0 auto 14px", display:"block" }}/>
              <div style={{ fontSize:16, color:C.forest, fontWeight:600 }}>Drop a CSV file here</div>
              <div style={{ fontSize:12, color:C.tan, marginTop:6 }}>or click to browse</div>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:16, flexWrap:"wrap", gap:10 }}>
              <div style={{ fontSize:12, color:C.tan }}>
                Required columns: <strong style={{color:C.forest}}>name</strong>, <strong style={{color:C.forest}}>categoryId</strong>, <strong style={{color:C.forest}}>price</strong>
              </div>
              <Btn variant="ghost" size="sm" onClick={downloadTemplate}><Download size={12}/>Download template</Btn>
            </div>
          </div>
        )}

        {/* STEP 1.5: PARSING */}
        {parsing && (
          <div style={{ padding:60, textAlign:"center" }}>
            <Loader2 size={28} color={C.forest} style={{ animation:"spin 1s linear infinite" }}/>
            <div style={{ marginTop:10, color:C.tan, fontSize:13 }}>Parsing CSV…</div>
          </div>
        )}

        {/* STEP 2: MAPPING + PREVIEW */}
        {file && !parsing && !results && (
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
            {/* File card */}
            <div style={{ background:C.cream, borderRadius:12, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <FileText size={18} color={C.forest}/>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:C.forest }}>{file.name}</div>
                  <div style={{ fontSize:11, color:C.tan }}>{rows.length} rows · {headers.length} columns</div>
                </div>
              </div>
              <button onClick={reset} style={{ background:"none", border:"none", cursor:"pointer", color:C.tan }}><X size={16}/></button>
            </div>

            {/* Mapping */}
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:C.tan, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>Column Mapping</div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))", gap:10 }}>
                {headers.map(h => {
                  const takenByOther = (field) => Object.entries(mapping).find(([col,mapped])=>mapped===field && col!==h);
                  return (
                    <div key={h} style={{ border:"1.5px solid rgba(31,61,58,0.1)", borderRadius:10, padding:"8px 10px", background:"#fff", display:"flex", alignItems:"center", gap:8 }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:10, color:C.tan, textTransform:"uppercase" }}>CSV column</div>
                        <div style={{ fontSize:12, fontWeight:700, color:C.forest, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{h}</div>
                      </div>
                      <span style={{ color:"rgba(31,61,58,0.3)" }}>→</span>
                      <select value={mapping[h] ?? CSV_IGNORE} onChange={e=>setMapping({...mapping, [h]: e.target.value})}
                        style={{ border:"1.5px solid rgba(31,61,58,0.15)", borderRadius:8, padding:"5px 8px", fontSize:12, outline:"none", background:"#fff", color:C.forest, fontFamily:"'Poiret One', cursive", cursor:"pointer", minWidth:130 }}>
                        <option value={CSV_IGNORE}>— ignore —</option>
                        {CSV_SCHEMA_FIELDS.map(f => {
                          const tb = takenByOther(f.key);
                          return <option key={f.key} value={f.key} disabled={!!tb}>{f.label}{f.required?" *":""}{tb?` (used)`:""}</option>;
                        })}
                      </select>
                    </div>
                  );
                })}
              </div>
              {unmappedRequired.length > 0 && (
                <div style={{ marginTop:10, padding:"10px 12px", background:"#fef3c7", border:"1px solid #fde68a", borderRadius:10, fontSize:12, color:"#92400e", display:"flex", alignItems:"center", gap:8 }}>
                  <AlertCircle size={14}/>Required fields not mapped: <strong>{unmappedRequired.map(f=>f.label).join(", ")}</strong>
                </div>
              )}
            </div>

            {/* Preview */}
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <div style={{ fontSize:11, fontWeight:700, color:C.tan, textTransform:"uppercase", letterSpacing:"0.07em" }}>Preview</div>
                <div style={{ fontSize:12, display:"flex", gap:10 }}>
                  <span style={{ color:"#15803d", fontWeight:700 }}>{preview.valid.length} valid</span>
                  <span style={{ color:"#b91c1c", fontWeight:700 }}>{preview.invalid.length} invalid</span>
                </div>
              </div>
              <div style={{ border:"1px solid rgba(31,61,58,0.08)", borderRadius:12, overflow:"hidden", maxHeight:280, overflowY:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                  <thead style={{ position:"sticky", top:0, background:C.cream, zIndex:1 }}>
                    <tr>
                      <th style={{ padding:"8px 10px", textAlign:"left", fontSize:10, color:C.tan, textTransform:"uppercase", letterSpacing:"0.05em" }}>#</th>
                      <th style={{ padding:"8px 10px", textAlign:"left", fontSize:10, color:C.tan, textTransform:"uppercase", letterSpacing:"0.05em" }}>Status</th>
                      {CSV_SCHEMA_FIELDS.map(f => <th key={f.key} style={{ padding:"8px 10px", textAlign:"left", fontSize:10, color:C.tan, textTransform:"uppercase", letterSpacing:"0.05em", whiteSpace:"nowrap" }}>{f.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {[...preview.invalid, ...preview.valid].slice(0,50).map(({idx, product, errors}) => (
                      <tr key={idx} style={{ borderTop:"1px solid rgba(31,61,58,0.06)" }}>
                        <td style={{ padding:"7px 10px", color:"#bbb" }}>{idx+1}</td>
                        <td style={{ padding:"7px 10px" }}>
                          {errors?.length
                            ? <span title={errors.join(", ")} style={{ color:"#b91c1c", display:"inline-flex", alignItems:"center", gap:4, fontWeight:700 }}><AlertCircle size={12}/>error</span>
                            : <span style={{ color:"#15803d", display:"inline-flex", alignItems:"center", gap:4, fontWeight:700 }}><CheckCircle2 size={12}/>ready</span>}
                        </td>
                        {CSV_SCHEMA_FIELDS.map(f => (
                          <td key={f.key} style={{ padding:"7px 10px", color:C.forest, maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                            {product[f.key] ?? <span style={{color:"#ccc"}}>—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rows.length > 50 && <div style={{ padding:"8px 12px", fontSize:11, color:C.tan, background:"#fafaf8", borderTop:"1px solid rgba(31,61,58,0.06)" }}>Showing first 50 of {rows.length} rows.</div>}
              </div>
            </div>

            {/* Footer actions */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", paddingTop:10, borderTop:"1px solid rgba(31,61,58,0.08)", marginTop:4 }}>
              <div style={{ fontSize:12, color:C.tan }}>
                Ready to import <strong style={{color:C.forest}}>{preview.valid.length}</strong> product{preview.valid.length!==1?"s":""}
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <Btn variant="ghost" onClick={handleClose} disabled={importing}>Cancel</Btn>
                <Btn onClick={runImport} disabled={importing || !preview.valid.length || unmappedRequired.length>0}>
                  {importing ? <Loader2 size={14} style={{animation:"spin 1s linear infinite"}}/> : <Upload size={14}/>}
                  Import {preview.valid.length}
                </Btn>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: RESULTS */}
        {results && (
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:12, padding:16 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, color:"#166534", fontSize:13, fontWeight:700 }}>
                  <CheckCircle2 size={16}/>Created
                </div>
                <div style={{ fontSize:32, fontWeight:800, color:"#14532d", marginTop:4 }}>{results.created}</div>
              </div>
              <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:12, padding:16 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, color:"#991b1b", fontSize:13, fontWeight:700 }}>
                  <AlertCircle size={16}/>Skipped
                </div>
                <div style={{ fontSize:32, fontWeight:800, color:"#7f1d1d", marginTop:4 }}>{results.skipped.length}</div>
              </div>
            </div>

            {results.skipped.length > 0 && (
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:C.tan, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>Skipped rows</div>
                <div style={{ border:"1px solid rgba(31,61,58,0.08)", borderRadius:12, maxHeight:220, overflowY:"auto" }}>
                  {results.skipped.map((s, i) => (
                    <div key={i} style={{ padding:"10px 14px", borderBottom:"1px solid rgba(31,61,58,0.06)", fontSize:12 }}>
                      <div style={{ color:C.forest, fontWeight:700 }}>Row {s.index !== undefined ? s.index + 1 : "?"}</div>
                      <div style={{ color:"#b91c1c", marginTop:2 }}>{s.error}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display:"flex", justifyContent:"flex-end", gap:8, paddingTop:10, borderTop:"1px solid rgba(31,61,58,0.08)" }}>
              <Btn variant="ghost" onClick={reset}>Import another</Btn>
              <Btn onClick={handleClose}><Check size={14}/>Done</Btn>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ─── PRODUCTS SECTION ─────────────────────────────────────────────────────────
function ProductsSection({ toast }) {
  const { data: productsRes, loading, error, reload, setData: setProductsRes } = useResource(() => api.getProducts("&includeMetadata=true"));
  const { data: categories } = useResource(() => api.getCategories());
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [modal, setModal] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const emptyForm = { name:"", categoryId:"", price:"", origin:"", tag:"", imageUrl:"", description:"", rating:"" };
  const [form, setForm] = useState(emptyForm);
  const products = productsRes?.data || productsRes || [];
  const cats = categories || [];
  const filtered = products.filter(p => (filterCat==="all"||String(p.categoryId)===filterCat) && p.name?.toLowerCase().includes(search.toLowerCase()));
  const openAdd  = () => { setForm({...emptyForm, categoryId:String(cats[0]?.id||"")}); setImageFile(null); setImagePreview(null); setModal({mode:"add"}); };
  const openEdit = p => { setForm({name:p.name||"",categoryId:String(p.categoryId),price:String(p.price),origin:p.origin||"",tag:p.tag||"",imageUrl:p.imageUrl||"",description:p.description||"",rating:String(p.rating||"")}); setImageFile(null); setImagePreview(p.imageUrl?.startsWith("/uploads")?`${API}${p.imageUrl}`:p.imageUrl||null); setModal({mode:"edit",item:p}); };
  const handleImageChange = e => { const f=e.target.files[0]; if(!f) return; setImageFile(f); setImagePreview(URL.createObjectURL(f)); };
  const buildFD = async () => {
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => { if (v !== "") fd.append(k, v); });
    if (imageFile) fd.append("image", await optimizeProductImage(imageFile));
    return fd;
  };
  const save = async () => {
    if (!form.name || !form.price || saving) return;
    setSaving(true);
    const mode = modal?.mode;
    const localPreview = imageFile ? imagePreview : null;
    try {
      const fd = await buildFD();
      if (mode === "add") {
        const created = await api.createProduct(fd);
        setProductsRes(prev => mergeProductResponseIntoCache(prev, created, localPreview));
        toast(created.imageUploadPending ? "Product created — image is processing" : "Product created", "success");
        setModal(null);
        setImageFile(null);
        setImagePreview(null);
        if (created.imageUploadPending) setTimeout(reload, 3500);
      } else {
        const updated = await api.updateProduct(modal.item.id, fd);
        setProductsRes(prev => replaceProductInCache(prev, updated, localPreview));
        toast("Product updated", "success");
        setModal(null);
        setImageFile(null);
        setImagePreview(null);
        if (imageFile) setTimeout(reload, 2500);
      }
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };
  const del  = async () => { setSaving(true); try { await api.deleteProduct(deleteId); toast("Product deleted","success"); setDeleteId(null); reload(); } catch(e){ toast(e.message,"error"); } finally { setSaving(false); } };
  const catOptions = [{value:"all",label:"All Categories"},...cats.map(c=>({value:String(c.id),label:c.name}))];
  if (loading) return <Loading/>;
  if (error)   return <SectionError error={error} reload={reload}/>;
  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div className="section-header" style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          <div style={{position:"relative"}}><Search size={14} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:C.tan}}/><Inp value={search} onChange={setSearch} placeholder="Search products…" style={{paddingLeft:32,minWidth:200}}/></div>
          <Sel value={filterCat} onChange={setFilterCat} options={catOptions} style={{width:"auto",minWidth:160}}/>
        </div>
        <div style={{display:"flex",gap:8}}>
          <Btn variant="ghost" onClick={reload}><RefreshCw size={14}/>Refresh</Btn>
          <Btn variant="gold" onClick={()=>setCsvImportOpen(true)}><FileSpreadsheet size={14}/>Import CSV</Btn>
          <Btn onClick={openAdd}><Plus size={14}/>Add Product</Btn>
        </div>
      </div>
      <div style={{background:"#fff",borderRadius:16,overflow:"hidden",border:"1px solid rgba(31,61,58,0.08)"}}>
        <Table columns={[
          {key:"imageUrl",label:"Image",render:v=>{ const src=v?.startsWith("/uploads")?`${API}${v}`:v; return src?<img src={src} style={{width:40,height:40,objectFit:"cover",borderRadius:10}} alt=""/>:<div style={{width:40,height:40,background:C.cream,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center"}}><ImageIcon size={14} color={C.tan}/></div>; }},
          {key:"name",label:"Name"},
          {key:"categoryId",label:"Category",render:v=>cats.find(c=>c.id===v)?.name||"—"},
          {key:"price",label:"Price",render:v=>`${v} MAD`},
          {key:"tag",label:"Tag",render:v=>v?<Badge variant="gold">{v}</Badge>:<span style={{color:"#ccc"}}>—</span>},
          {key:"rating",label:"Rating",render:v=>v?`⭐ ${v}`:"—"},
          {key:"origin",label:"Origin"},
          {key:"actions",label:"Actions",render:(_,row)=>(
            <div style={{display:"flex",gap:6}}>
              <Btn size="sm" variant="ghost" onClick={()=>openEdit(row)}><Pencil size={12}/></Btn>
              <Btn size="sm" variant="danger" onClick={()=>setDeleteId(row.id)}><Trash2 size={12}/></Btn>
            </div>
          )},
        ]} data={filtered}/>
      </div>
      <Modal open={!!modal} onClose={()=>setModal(null)} title={modal?.mode==="add"?"Add Product":"Edit Product"}>
        <FF label="Product Image">
          <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
            <label style={{flex:1,border:"2px dashed rgba(31,61,58,0.2)",borderRadius:12,padding:"14px 16px",cursor:"pointer",display:"flex",alignItems:"center",gap:10,color:C.tan,fontSize:13}}>
              <Upload size={16}/>{imageFile?imageFile.name:"Upload image (jpg, png, webp)"}
              <input type="file" accept="image/*" onChange={handleImageChange} style={{display:"none"}}/>
            </label>
            {imagePreview&&<img src={imagePreview} style={{width:60,height:60,objectFit:"cover",borderRadius:10,flexShrink:0}} alt=""/>}
          </div>
          <div style={{marginTop:6,fontSize:11,color:"#aaa"}}>Or paste an external URL below</div>
        </FF>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 16px"}}>
          <FF label="Name *"><Inp value={form.name} onChange={v=>setForm(f=>({...f,name:v}))} placeholder="Product name"/></FF>
          <FF label="Category"><Sel value={String(form.categoryId)} onChange={v=>setForm(f=>({...f,categoryId:v}))} options={cats.map(c=>({value:String(c.id),label:c.name}))}/></FF>
          <FF label="Price (MAD) *"><Inp value={form.price} onChange={v=>setForm(f=>({...f,price:v}))} placeholder="0.00" type="number"/></FF>
          <FF label="Rating (0-5)"><Inp value={form.rating} onChange={v=>setForm(f=>({...f,rating:v}))} placeholder="4.5" type="number"/></FF>
          <FF label="Origin"><Inp value={form.origin} onChange={v=>setForm(f=>({...f,origin:v}))} placeholder="Country"/></FF>
          <FF label="Tag"><Inp value={form.tag} onChange={v=>setForm(f=>({...f,tag:v}))} placeholder="Bestseller, New…"/></FF>
        </div>
        <FF label="Image URL (fallback)"><Inp value={form.imageUrl} onChange={v=>setForm(f=>({...f,imageUrl:v}))} placeholder="https://…"/></FF>
        <FF label="Description">
          <textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="Short description…" rows={3}
            style={{width:"100%",border:"1.5px solid rgba(31,61,58,0.15)",borderRadius:10,padding:"8px 12px",fontSize:13,fontFamily:"'Poiret One', cursive",resize:"vertical",outline:"none",color:C.forest,boxSizing:"border-box"}}/>
        </FF>
        <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:8}}>
          <Btn variant="ghost" onClick={()=>setModal(null)}>Cancel</Btn>
          <Btn onClick={save} disabled={saving}>{saving?<Loader2 size={14}/>:<Check size={14}/>}{modal?.mode==="add"?"Create":"Save"}</Btn>
        </div>
      </Modal>
      <ConfirmModal open={!!deleteId} onClose={()=>setDeleteId(null)} onConfirm={del} loading={saving}/>
      <ProductCsvImport
        open={csvImportOpen}
        onClose={()=>setCsvImportOpen(false)}
        onSuccess={reload}
        toast={toast}
        categories={cats}
      />
    </div>
  );
}

// ─── METADATA SECTION ─────────────────────────────────────────────────────────
function MetadataSection({ toast }) {
  const { data: productsRes } = useResource(() => api.getProducts());
  const [allMeta, setAllMeta] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [search, setSearch] = useState("");
  const [filterProd, setFilterProd] = useState("all");
  const [modal, setModal] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ productId:"", key:"", value:"" });
  const products = productsRes?.data || productsRes || [];
  const loadAllMeta = useCallback(async () => {
    if (!products.length) return; setLoadingMeta(true);
    try { const res = await Promise.all(products.map(p=>api.getMetadata(p.id).then(m=>m.map(item=>({...item,productId:p.id}))).catch(()=>[]))); setAllMeta(res.flat()); }
    catch(e){} finally { setLoadingMeta(false); }
  }, [products.length]);
  useEffect(()=>{ loadAllMeta(); }, [loadAllMeta]);
  const filtered = allMeta.filter(m=>(filterProd==="all"||String(m.productId)===filterProd)&&(m.key?.toLowerCase().includes(search.toLowerCase())||m.value?.toLowerCase().includes(search.toLowerCase())));
  const openAdd  = () => { setForm({productId:String(products[0]?.id||""),key:"type",value:""}); setModal({mode:"add"}); };
  const openEdit = m => { setForm({productId:String(m.productId),key:m.key,value:m.value}); setModal({mode:"edit",item:m}); };
  const save = async () => { if(!form.key||!form.value) return; setSaving(true); try { if(modal.mode==="add"){await api.createMetadata({productId:Number(form.productId),key:form.key,value:form.value});toast("Metadata created","success");} else {await api.updateMetadata(modal.item.id,{value:form.value});toast("Metadata updated","success");} setModal(null); loadAllMeta(); } catch(e){toast(e.message,"error");} finally{setSaving(false);} };
  const del  = async () => { setSaving(true); try{await api.deleteMetadata(deleteId);toast("Metadata deleted","success");setDeleteId(null);loadAllMeta();}catch(e){toast(e.message,"error");}finally{setSaving(false);} };
  const keyOptions = ["type","skin","material","style","size","color","other"].map(k=>({value:k,label:k}));
  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div className="section-header" style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          <div style={{position:"relative"}}><Search size={14} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:C.tan}}/><Inp value={search} onChange={setSearch} placeholder="Search keys/values…" style={{paddingLeft:32,minWidth:200}}/></div>
          <Sel value={filterProd} onChange={setFilterProd} options={[{value:"all",label:"All Products"},...products.map(p=>({value:String(p.id),label:p.name}))]} style={{width:"auto",minWidth:160}}/>
        </div>
        <Btn onClick={openAdd}><Plus size={14}/>Add Metadata</Btn>
      </div>
      <div style={{background:"#fff",borderRadius:16,overflow:"hidden",border:"1px solid rgba(31,61,58,0.08)"}}>
        {loadingMeta?<Loading/>:<Table columns={[
          {key:"id",label:"#"},
          {key:"productId",label:"Product",render:v=>products.find(p=>p.id===v)?.name||v},
          {key:"key",label:"Key",render:v=><Badge variant="sand">{v}</Badge>},
          {key:"value",label:"Value"},
          {key:"actions",label:"Actions",render:(_,row)=>(
            <div style={{display:"flex",gap:6}}>
              <Btn size="sm" variant="ghost" onClick={()=>openEdit(row)}><Pencil size={12}/></Btn>
              <Btn size="sm" variant="danger" onClick={()=>setDeleteId(row.id)}><Trash2 size={12}/></Btn>
            </div>
          )},
        ]} data={filtered}/>}
      </div>
      <Modal open={!!modal} onClose={()=>setModal(null)} title={modal?.mode==="add"?"Add Metadata":"Edit Metadata"}>
        {modal?.mode==="add"&&<FF label="Product"><Sel value={form.productId} onChange={v=>setForm(f=>({...f,productId:v}))} options={products.map(p=>({value:String(p.id),label:p.name}))}/></FF>}
        <FF label="Key"><Sel value={form.key} onChange={v=>setForm(f=>({...f,key:v}))} options={keyOptions}/></FF>
        <FF label="Value"><Inp value={form.value} onChange={v=>setForm(f=>({...f,value:v}))} placeholder="e.g. Cotton"/></FF>
        <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:8}}>
          <Btn variant="ghost" onClick={()=>setModal(null)}>Cancel</Btn>
          <Btn onClick={save} disabled={saving}>{saving?<Loader2 size={14}/>:<Check size={14}/>}{modal?.mode==="add"?"Create":"Save"}</Btn>
        </div>
      </Modal>
      <ConfirmModal open={!!deleteId} onClose={()=>setDeleteId(null)} onConfirm={del} loading={saving}/>
    </div>
  );
}


// ─── ORDER ITEM / CUSTOMER RENDER HELPERS ─────────────────────────────────────
const moneyMAD = (value) => `${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} MAD`;

function getOrderItems(orderOrData) {
  const source = orderOrData?.orderData ?? orderOrData;
  if (Array.isArray(source?.items)) return source.items;
  if (Array.isArray(source)) return source;
  return [];
}

function getOrderCustomer(order) {
  return order?.orderData?.customer || {};
}

function itemQty(item) {
  return Number(item?.quantity ?? item?.qty ?? 1) || 1;
}

function itemLineTotal(item) {
  return (Number(item?.price || 0) * itemQty(item));
}

function getOrderItemCount(order) {
  return getOrderItems(order).reduce((sum, item) => sum + itemQty(item), 0);
}

function resolveOrderImage(item) {
  const src = item?.img || item?.imageUrl || item?.image || item?.photo || "";
  if (!src) return "";
  if (/^https?:\/\//i.test(src)) return src;
  if (src.startsWith("/api/")) return `${API}${src}`;
  if (src.startsWith("/uploads")) return `${API}${src}`;
  return src;
}

function CustomerPreview({ order }) {
  const customer = getOrderCustomer(order);
  const name = customer.name || order?.user?.name || order?.user?.email || "Guest client";
  const phone = customer.phone || customer.tel || customer.whatsapp || "";
  const address = order?.deliveryAddress || [customer.address, customer.city, customer.zip].filter(Boolean).join(", ");

  return (
    <div style={{ minWidth: 190 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: C.forest }}>{name}</div>
      {phone && (
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: C.tan, marginTop: 4 }}>
          <Phone size={11}/>{phone}
        </div>
      )}
      {address && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 5, fontSize: 11, color: "#9a8b7b", marginTop: 4, lineHeight: 1.35 }}>
          <MapPin size={11} style={{ flexShrink: 0, marginTop: 1 }}/>
          <span>{address}</span>
        </div>
      )}
    </div>
  );
}

function OrderItemsPreview({ order, compact = false }) {
  const items = getOrderItems(order);
  const visible = compact ? items.slice(0, 2) : items.slice(0, 3);
  const hidden = Math.max(0, items.length - visible.length);
  const totalQty = getOrderItemCount(order);

  if (!items.length) {
    return <span style={{ fontSize: 12, color: "#aaa", fontStyle: "italic" }}>No item details</span>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7, minWidth: compact ? 210 : 280 }}>
      <div style={{ fontSize: 10, color: C.tan, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>
        {items.length} product{items.length !== 1 ? "s" : ""} · {totalQty} piece{totalQty !== 1 ? "s" : ""}
      </div>
      {visible.map((item, i) => {
        const img = resolveOrderImage(item);
        const qty = itemQty(item);
        return (
          <div key={`${item?.id || item?.name || "item"}-${i}`} style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: C.cream, overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(31,61,58,0.07)" }}>
              {img ? <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }}/> : <Package size={13} color={C.gold}/>} 
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12, color: C.forest, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {item?.name || `Product #${item?.id || i + 1}`}
              </div>
              <div style={{ fontSize: 11, color: C.tan, marginTop: 1 }}>
                Qty ×{qty} · {moneyMAD(item?.price)} each · <strong>{moneyMAD(itemLineTotal(item))}</strong>
              </div>
              {(item?.size || item?.color || item?.tag) && (
                <div style={{ fontSize: 10, color: "#aaa", marginTop: 1 }}>
                  {[item.size && `Size: ${item.size}`, item.color && `Color: ${item.color}`, item.tag].filter(Boolean).join(" · ")}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {hidden > 0 && <div style={{ fontSize: 11, color: C.tan, fontWeight: 700 }}>+{hidden} more product{hidden !== 1 ? "s" : ""}</div>}
    </div>
  );
}

function OrderDetailsModal({ order, onClose }) {
  const items = getOrderItems(order);
  const customer = getOrderCustomer(order);
  const address = order?.deliveryAddress || [customer.address, customer.city, customer.zip].filter(Boolean).join(", ") || "—";
  const customerName = customer.name || order?.user?.name || order?.user?.email || "Guest client";
  const phone = customer.phone || customer.tel || customer.whatsapp || "—";

  return (
    <Modal open={!!order} onClose={onClose} title={order ? `Order #${order.id} details` : "Order details"}>
      {order && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
            <div style={{ background: C.cream, borderRadius: 14, padding: "12px 14px" }}>
              <div style={{ fontSize: 10, color: C.tan, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em" }}>Client</div>
              <div style={{ fontSize: 14, color: C.forest, fontWeight: 800, marginTop: 5 }}>{customerName}</div>
              <div style={{ fontSize: 12, color: C.tan, marginTop: 4 }}>{phone}</div>
            </div>
            <div style={{ background: C.cream, borderRadius: 14, padding: "12px 14px" }}>
              <div style={{ fontSize: 10, color: C.tan, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em" }}>Address</div>
              <div style={{ fontSize: 12, color: C.forest, lineHeight: 1.5, marginTop: 5 }}>{address}</div>
            </div>
            <div style={{ background: C.cream, borderRadius: 14, padding: "12px 14px" }}>
              <div style={{ fontSize: 10, color: C.tan, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em" }}>Total</div>
              <div style={{ fontSize: 18, color: C.forest, fontWeight: 900, marginTop: 5 }}>{moneyMAD(order.totalAmount)}</div>
              <div style={{ fontSize: 11, color: C.tan, marginTop: 3 }}>{getOrderItemCount(order)} piece{getOrderItemCount(order) !== 1 ? "s" : ""}</div>
            </div>
          </div>

          <div style={{ border: "1px solid rgba(31,61,58,0.08)", borderRadius: 16, overflow: "hidden" }}>
            <div style={{ background: C.forest, color: C.gold, padding: "10px 14px", fontSize: 12, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Products to deliver
            </div>
            <div style={{ padding: "8px 14px" }}>
              {items.length === 0 ? (
                <div style={{ padding: 20, textAlign: "center", color: "#aaa", fontSize: 13 }}>No product details saved for this order.</div>
              ) : items.map((item, i) => {
                const img = resolveOrderImage(item);
                const qty = itemQty(item);
                return (
                  <div key={`${item?.id || item?.name || "item"}-${i}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 0", borderBottom: i < items.length - 1 ? "1px solid rgba(31,61,58,0.07)" : "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                      <div style={{ width: 48, height: 48, borderRadius: 13, background: C.cream, overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {img ? <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }}/> : <Package size={16} color={C.gold}/>} 
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14, color: C.forest, fontWeight: 900 }}>{item?.name || `Product #${item?.id || i + 1}`}</div>
                        <div style={{ fontSize: 11, color: C.tan, marginTop: 2 }}>
                          Product ID: {item?.id || "—"}{item?.size ? ` · Size: ${item.size}` : ""}{item?.color ? ` · Color: ${item.color}` : ""}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 13, color: C.forest, fontWeight: 900 }}>×{qty}</div>
                      <div style={{ fontSize: 12, color: C.tan }}>{moneyMAD(itemLineTotal(item))}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {(customer.note || order.deliveryNote) && (
            <div style={{ background: "rgba(223,204,37,0.10)", border: "1px solid rgba(223,204,37,0.22)", borderRadius: 14, padding: "12px 14px", fontSize: 12, color: C.forest }}>
              <strong>Note:</strong> {order.deliveryNote || customer.note}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ─── ORDERS SECTION ───────────────────────────────────────────────────────────
function OrdersSection({ toast }) {
  const { data: orders, loading, error, reload } = useResource(() => api.getOrders());
  const { data: deliveryUsers, reload: reloadDU } = useResource(() => api.getDeliveryUsers()); // !NEW
  const [filter, setFilter] = useState("all");
  const [deleteId, setDeleteId] = useState(null);
  const [viewOrder, setViewOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  // !NEW — assignment modal state
  const [assignOrder, setAssignOrder] = useState(null);
  const [pickedDU, setPickedDU] = useState("");
  const [note, setNote] = useState("");
  const [assigning, setAssigning] = useState(false);

  const all = Array.isArray(orders) ? orders : orders?.data || [];
  const duList = Array.isArray(deliveryUsers) ? deliveryUsers : [];

  // !NEW — expanded filter set covering delivery pipeline
  const filtered = all.filter(o => {
    if (filter === "all") return true;
    if (filter === "confirmed") return o.isConfirmed;
    if (filter === "pending")   return !o.isConfirmed;
    if (filter === "unassigned")return o.isConfirmed && (!o.deliveryStatus || o.deliveryStatus === "UNASSIGNED");
    if (filter === "assigned")  return o.deliveryStatus === "ASSIGNED" || o.deliveryStatus === "ACCEPTED";
    if (filter === "in_delivery") return o.deliveryStatus === "OUT_FOR_DELIVERY";
    if (filter === "delivered") return o.deliveryStatus === "DELIVERED";
    if (filter === "failed")    return o.deliveryStatus === "FAILED";
    return true;
  });

  const toggle = async o => { try{await api.updateOrder(o.id,{isConfirmed:!o.isConfirmed});toast(`Order #${o.id} ${!o.isConfirmed?"confirmed":"set to pending"}`,"success");reload();}catch(e){toast(e.message,"error");} };
  const del = async () => { setSaving(true); try{await api.deleteOrder(deleteId);toast("Order deleted","success");setDeleteId(null);reload();}catch(e){toast(e.message,"error");}finally{setSaving(false);} };

  // !NEW — open assign modal (allowed only for confirmed + not-delivered orders)
  const canAssign = (o) => o.isConfirmed && o.deliveryStatus !== "DELIVERED";
  const openAssign = (o) => {
    setAssignOrder(o);
    setPickedDU(o.deliveryUserId ? String(o.deliveryUserId) : "");
    setNote(o.deliveryNote || "");
  };
  const confirmAssign = async () => {
    if (!pickedDU) { toast("Pick a delivery user", "error"); return; }
    setAssigning(true);
    try {
      await api.assignDelivery(assignOrder.id, parseInt(pickedDU, 10), note || undefined);
      toast(`Order #${assignOrder.id} assigned`, "success");
      setAssignOrder(null); setPickedDU(""); setNote("");
      reload();
    } catch (e) { toast(e.message, "error"); }
    finally { setAssigning(false); }
  };

  // !NEW — status badge helper
  const dStatusVariant = (s) => ({
    UNASSIGNED: "sand", ASSIGNED: "gold", ACCEPTED: "gold",
    OUT_FOR_DELIVERY: "default", DELIVERED: "success", FAILED: "danger",
  }[s] || "sand");

  const TABS = [
    ["all",         "All"],
    ["confirmed",   "Confirmed"],
    ["pending",     "Pending"],
    ["unassigned",  "Unassigned"],
    ["assigned",    "Assigned"],
    ["in_delivery", "In delivery"],
    ["delivered",   "Delivered"],
    ["failed",      "Failed"],
  ];

  if (loading) return <Loading/>;
  if (error)   return <SectionError error={error} reload={reload}/>;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {TABS.map(([f, label])=>(
            <button key={f} onClick={()=>setFilter(f)} style={{padding:"7px 18px",borderRadius:10,border:"1.5px solid rgba(31,61,58,0.15)",background:filter===f?C.forest:"#fff",color:filter===f?C.gold:C.forest,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Poiret One', cursive"}}>{label}</button>
          ))}
        </div>
        <Btn variant="ghost" onClick={()=>{reload();reloadDU();}}><RefreshCw size={14}/>Refresh</Btn>
      </div>

      <div style={{background:"#fff",borderRadius:16,overflow:"hidden",border:"1px solid rgba(31,61,58,0.08)"}}>
        <Table columns={[
          {key:"id",label:"Order #",render:v=>`#${v}`},
          {key:"customer",label:"Client",render:(_, row)=><CustomerPreview order={row}/>},
          {key:"orderData",label:"Ordered items",render:(_, row)=><OrderItemsPreview order={row}/>},
          {key:"totalAmount",label:"Total",render:v=><strong>{moneyMAD(v)}</strong>},
          {key:"isConfirmed",label:"Status",render:v=><Badge variant={v?"success":"sand"}>{v?"Confirmed":"Pending"}</Badge>},
          // !NEW — delivery person column
          {key:"deliveryUser",label:"Delivery",render:(v, row)=> v
            ? <span style={{fontSize:12,color:C.forest}}>{v.name || v.email}</span>
            : <span style={{fontSize:11,color:"#aaa",fontStyle:"italic"}}>unassigned</span>
          },
          // !NEW — delivery status column
          {key:"deliveryStatus",label:"D. status",render:v=>{
            const s = v || "UNASSIGNED";
            return <Badge variant={dStatusVariant(s)}>{s.replace(/_/g," ").toLowerCase()}</Badge>;
          }},
          // !NEW — assigned / delivered timestamps (compact)
          {key:"assignedAt",label:"Assigned",render:v=>v?String(v).slice(0,10):"—"},
          {key:"deliveredAt",label:"Delivered",render:v=>v?String(v).slice(0,10):"—"},
          {key:"actions",label:"Actions",render:(_,row)=>(
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              <Btn size="sm" variant="ghost" onClick={()=>setViewOrder(row)}><Eye size={12}/>View</Btn>
              <Btn size="sm" variant={row.isConfirmed?"danger":"gold"} onClick={()=>toggle(row)}>{row.isConfirmed?"Unconfirm":"Confirm"}</Btn>
              {canAssign(row) && (
                <Btn size="sm" variant="primary" onClick={()=>openAssign(row)}>
                  <Truck size={12}/>{row.deliveryUserId ? "Reassign" : "Assign"}
                </Btn>
              )}
              <Btn size="sm" variant="danger" onClick={()=>setDeleteId(row.id)}><Trash2 size={12}/></Btn>
            </div>
          )},
        ]} data={filtered} emptyMsg="No orders match this filter"/>
      </div>

      <ConfirmModal open={!!deleteId} onClose={()=>setDeleteId(null)} onConfirm={del} loading={saving}/>
      <OrderDetailsModal order={viewOrder} onClose={()=>setViewOrder(null)}/>

      {/* !NEW — assignment modal */}
      <Modal open={!!assignOrder} onClose={()=>!assigning&&setAssignOrder(null)}
        title={assignOrder?.deliveryUserId ? `Reassign Order #${assignOrder?.id}` : `Assign Order #${assignOrder?.id}`}>
        {assignOrder && (
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {/* Summary */}
            <div style={{background:C.cream,borderRadius:12,padding:"12px 14px",fontSize:12,color:C.forest,display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
              <div><strong style={{color:C.tan}}>Total:</strong> {assignOrder.totalAmount} MAD</div>
              <div><strong style={{color:C.tan}}>Customer:</strong> {assignOrder.user?.name || assignOrder.user?.email || "guest"}</div>
              {assignOrder.deliveryAddress && (
                <div style={{flex:"1 1 100%",fontSize:11,color:C.tan}}>📍 {assignOrder.deliveryAddress}</div>
              )}
            </div>

            <FF label="Delivery user">
              {duList.length === 0 ? (
                <div style={{padding:"14px 12px",background:"rgba(239,68,68,0.08)",color:"#b91c1c",borderRadius:10,fontSize:12,display:"flex",alignItems:"center",gap:8}}>
                  <AlertCircle size={14}/>No users with role DELIVERY. Create one first in the Users section.
                </div>
              ) : (
                <Sel value={pickedDU} onChange={setPickedDU}
                  options={[
                    {value:"", label:"— Select delivery user —"},
                    ...duList.map(u=>({ value:String(u.id), label:`${u.name || u.email} (#${u.id})` }))
                  ]}/>
              )}
            </FF>

            <FF label="Note (optional)">
              <Inp value={note} onChange={setNote} placeholder="e.g. Call customer on arrival"/>
            </FF>

            <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:6}}>
              <Btn variant="ghost" onClick={()=>setAssignOrder(null)} disabled={assigning}>Cancel</Btn>
              <Btn variant="gold" onClick={confirmAssign} disabled={assigning || !pickedDU || duList.length===0}>
                {assigning ? <><Loader2 size={12} className="spin"/>Assigning…</> : <><UserCheck size={12}/>Confirm</>}
              </Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ─── REVIEWS SECTION ─────────────────────────────────────────────────────────
function ReviewsSection({ toast }) {
  const { data: productsRes } = useResource(() => api.getProducts());
  const [allReviews, setAllReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({ rating:"5", comment:"" });
  const products = productsRes?.data || productsRes || [];
  const loadReviews = useCallback(async () => {
    if (!products.length) return; setLoading(true);
    try { const res=await Promise.all(products.map(p=>api.getReviews(p.id).then(r=>r.map(rv=>({...rv,productId:p.id}))).catch(()=>[]))); setAllReviews(res.flat().sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt))); }
    catch(e){} finally{setLoading(false);}
  }, [products.length]);
  useEffect(()=>{ loadReviews(); }, [loadReviews]);
  const del = async () => { setSaving(true); try{await api.deleteReview(deleteId);toast("Review deleted","success");setDeleteId(null);loadReviews();}catch(e){toast(e.message,"error");}finally{setSaving(false);} };
  const saveEdit = async () => { setSaving(true); try{await api.updateReview(editModal.id,{rating:Number(editForm.rating),comment:editForm.comment});toast("Review updated","success");setEditModal(null);loadReviews();}catch(e){toast(e.message,"error");}finally{setSaving(false);} };
  const openEdit = r => { setEditForm({rating:String(r.rating),comment:r.comment||""}); setEditModal(r); };
  if (loading) return <Loading/>;
  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",justifyContent:"flex-end"}}>
        <Btn variant="ghost" onClick={loadReviews}><RefreshCw size={14}/>Refresh</Btn>
      </div>
      <div className="reviews-grid" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:14}}>
        {allReviews.map(r=>{
          const product=products.find(p=>p.id===r.productId);
          return (
            <div key={r.id} style={{background:"#fff",borderRadius:18,padding:"18px 20px",boxShadow:"0 2px 16px rgba(31,61,58,0.07)",border:"1px solid rgba(31,61,58,0.07)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                <div>
                  <div style={{fontWeight:700,fontSize:13,color:C.forest}}>{product?.name||`Product #${r.productId}`}</div>
                  <div style={{fontSize:11,color:"#aaa"}}>{r.createdAt?.slice(0,10)}</div>
                </div>
                <div style={{display:"flex",gap:4}}>
                  <Btn size="sm" variant="ghost" onClick={()=>openEdit(r)}><Pencil size={12}/></Btn>
                  <Btn size="sm" variant="danger" onClick={()=>setDeleteId(r.id)}><Trash2 size={12}/></Btn>
                </div>
              </div>
              <div style={{fontSize:18,marginBottom:8}}>{"⭐".repeat(Math.round(r.rating))}</div>
              <p style={{fontSize:13,color:C.tan,lineHeight:1.5,margin:0}}>{r.comment||<em style={{color:"#ccc"}}>No comment</em>}</p>
            </div>
          );
        })}
      </div>
      {allReviews.length===0&&<div style={{textAlign:"center",color:"#bbb",padding:40}}>No reviews yet.</div>}
      <Modal open={!!editModal} onClose={()=>setEditModal(null)} title="Edit Review">
        <FF label="Rating (1-5)"><Sel value={editForm.rating} onChange={v=>setEditForm(f=>({...f,rating:v}))} options={[1,2,3,4,5].map(n=>({value:String(n),label:`${n} ⭐`}))}/></FF>
        <FF label="Comment"><textarea value={editForm.comment} onChange={e=>setEditForm(f=>({...f,comment:e.target.value}))} rows={3} style={{width:"100%",border:"1.5px solid rgba(31,61,58,0.15)",borderRadius:10,padding:"8px 12px",fontSize:13,fontFamily:"'Poiret One', cursive",resize:"vertical",outline:"none",color:C.forest,boxSizing:"border-box"}}/></FF>
        <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:8}}>
          <Btn variant="ghost" onClick={()=>setEditModal(null)}>Cancel</Btn>
          <Btn onClick={saveEdit} disabled={saving}>{saving?<Loader2 size={14}/>:<Check size={14}/>}Save</Btn>
        </div>
      </Modal>
      <ConfirmModal open={!!deleteId} onClose={()=>setDeleteId(null)} onConfirm={del} loading={saving}/>
    </div>
  );
}

// ─── SESSIONS SECTION ─────────────────────────────────────────────────────────
function SessionsSection({ toast }) {
  const { data: sessions, loading, error, reload } = useResource(() => api.getSessions());
  const [clearing, setClearing] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const clearExpired = async () => { setClearing(true); try{const r=await api.deleteExpiredSessions();toast(`Deleted ${r?.deletedCount||0} expired sessions`,"success");reload();}catch(e){toast(e.message,"error");}finally{setClearing(false);} };
  const del = async () => { setSaving(true); try{await api.deleteSession(deleteId);toast("Session deleted","success");setDeleteId(null);reload();}catch(e){toast(e.message,"error");}finally{setSaving(false);} };
  if (loading) return <Loading/>;
  if (error)   return <SectionError error={error} reload={reload}/>;
  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
        <Btn variant="danger" onClick={clearExpired} disabled={clearing}>{clearing?<Loader2 size={14}/>:<Trash2 size={14}/>}Clear Expired</Btn>
        <Btn variant="ghost" onClick={reload}><RefreshCw size={14}/>Refresh</Btn>
      </div>
      <div style={{background:"#fff",borderRadius:16,overflow:"hidden",border:"1px solid rgba(31,61,58,0.08)"}}>
        <Table columns={[
          {key:"id",label:"Session ID",render:v=><span style={{fontFamily:"monospace",fontSize:11,background:C.cream,padding:"3px 8px",borderRadius:6}}>{String(v).slice(0,20)}…</span>},
          {key:"cartData",label:"Cart",render:v=>{ const count=Array.isArray(v?.items)?v.items.length:Array.isArray(v)?v.length:(v?.items??0); return `${count} item${count!==1?"s":""}`; }},
          {key:"confirmedAt",label:"Confirmed",render:v=>v?<Badge variant="success">Yes</Badge>:<Badge variant="sand">No</Badge>},
          {key:"expiresAt",label:"Expires",render:v=>{ if(!v) return "—"; const exp=new Date(v)<new Date(); return <span style={{color:exp?"#b91c1c":C.forest}}>{v.slice(0,10)}{exp?" (expired)":""}</span>; }},
          {key:"actions",label:"Actions",render:(_,row)=><Btn size="sm" variant="danger" onClick={()=>setDeleteId(row.id)}><Trash2 size={12}/></Btn>},
        ]} data={sessions||[]}/>
      </div>
      <ConfirmModal open={!!deleteId} onClose={()=>setDeleteId(null)} onConfirm={del} loading={saving}/>
    </div>
  );
}


// ─── CAMPAIGNS SECTION ───────────────────────────────────────────────────────
function CampaignsSection({ toast }) {
  const [form, setForm] = useState({
    audience: "USERS",
    subject: "",
    title: "",
    preheader: "",
    message: "",
    ctaLabel: "Shop now",
    ctaUrl: "https://daroulmes.store",
    customEmails: "",
  });
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [preview, setPreview] = useState({ count: 0, sample: [] });
  const [previewLoading, setPreviewLoading] = useState(false);

  const parseCustomEmails = useCallback((value) => {
    return String(value || "")
      .split(/[\n,;\s]+/)
      .map(e => e.trim().toLowerCase())
      .filter(Boolean)
      .filter((email, index, arr) => arr.indexOf(email) === index);
  }, []);

  const customEmailCount = useMemo(() => parseCustomEmails(form.customEmails).length, [form.customEmails, parseCustomEmails]);
  const recipientCount = form.audience === "CUSTOM" ? customEmailCount : preview.count;

  const loadPreview = useCallback(async () => {
    if (form.audience === "CUSTOM") {
      setPreview({ count: customEmailCount, sample: parseCustomEmails(form.customEmails).slice(0, 8).map(email => ({ email, role: "CUSTOM" })) });
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await api.getCampaignRecipients(form.audience);
      setPreview(res || { count: 0, sample: [] });
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setPreviewLoading(false);
    }
  }, [form.audience, form.customEmails, customEmailCount, parseCustomEmails, toast]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const canSend = form.subject.trim().length >= 3 && form.message.trim().length >= 10 && recipientCount > 0 && !sending;

  const sendCampaign = async () => {
    setSending(true);
    setLastResult(null);
    try {
      const payload = {
        audience: form.audience,
        subject: form.subject.trim(),
        title: form.title.trim(),
        preheader: form.preheader.trim(),
        message: form.message.trim(),
        ctaLabel: form.ctaLabel.trim(),
        ctaUrl: form.ctaUrl.trim(),
        customEmails: parseCustomEmails(form.customEmails),
      };
      const result = await api.sendCampaign(payload);
      setLastResult(result);
      toast(`Campaign sent: ${result.sent}/${result.total} delivered`, result.failed ? "error" : "success");
      await loadPreview();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSending(false);
    }
  };

  const audienceOptions = [
    { value: "USERS", label: "Customers only" },
    { value: "ALL", label: "All accounts" },
    { value: "CUSTOM", label: "Custom emails" },
  ];

  const textareaStyle = {
    width: "100%",
    border: "1.5px solid rgba(31,61,58,0.15)",
    borderRadius: 12,
    padding: "10px 12px",
    fontSize: 13,
    outline: "none",
    background: "#fff",
    color: C.forest,
    fontFamily: "'Poiret One', cursive",
    resize: "vertical",
    minHeight: 120,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="campaign-grid" style={{ display: "grid", gridTemplateColumns: "1.15fr .85fr", gap: 16, alignItems: "start" }}>
        <div style={{ background: "#fff", borderRadius: 20, padding: 22, border: "1px solid rgba(31,61,58,0.08)", boxShadow: "0 2px 16px rgba(31,61,58,0.06)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
            <div style={{ width: 42, height: 42, borderRadius: 14, background: C.gold + "24", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Megaphone size={20} color={C.forest} />
            </div>
            <div>
              <h3 style={{ fontFamily: "'Poiret One', cursive", fontSize: 20, color: C.forest, margin: 0 }}>Create SMTP Campaign</h3>
              <p style={{ fontSize: 12, color: C.tan, marginTop: 2 }}>Send product updates, new arrivals, discounts, and local offers.</p>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <FF label="Audience"><Sel value={form.audience} onChange={v => set("audience", v)} options={audienceOptions} /></FF>
            <FF label="Subject *"><Inp value={form.subject} onChange={v => set("subject", v)} placeholder="New arrivals are live 🔥" /></FF>
          </div>

          {form.audience === "CUSTOM" && (
            <FF label="Custom Emails">
              <textarea value={form.customEmails} onChange={e => set("customEmails", e.target.value)} placeholder="client1@email.com, client2@email.com" style={{ ...textareaStyle, minHeight: 80 }} />
              <p style={{ fontSize: 11, color: C.tan, marginTop: 6 }}>{customEmailCount} unique email(s) detected.</p>
            </FF>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <FF label="Hero Title"><Inp value={form.title} onChange={v => set("title", v)} placeholder="Fresh products just landed" /></FF>
            <FF label="Preview Text"><Inp value={form.preheader} onChange={v => set("preheader", v)} placeholder="A short text shown in the inbox preview" /></FF>
          </div>

          <FF label="Campaign Message *">
            <textarea value={form.message} onChange={e => set("message", e.target.value)} placeholder={"Write your offer here...\nExample: New perfumes, clothes, makeup and kitchen products are now available. Order with WhatsApp and pay on delivery."} style={textareaStyle} />
          </FF>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <FF label="CTA Label"><Inp value={form.ctaLabel} onChange={v => set("ctaLabel", v)} placeholder="Shop now" /></FF>
            <FF label="CTA URL"><Inp value={form.ctaUrl} onChange={v => set("ctaUrl", v)} placeholder="https://daroulmes.store" /></FF>
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
            <Btn variant="ghost" onClick={loadPreview} disabled={previewLoading}>{previewLoading ? <Loader2 size={14} /> : <RefreshCw size={14} />}Refresh recipients</Btn>
            <Btn onClick={sendCampaign} disabled={!canSend}>{sending ? <Loader2 size={14} /> : <Send size={14} />}Send Campaign</Btn>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ background: `linear-gradient(140deg, ${C.forest}, #2d5a54)`, borderRadius: 20, padding: 22, color: "#fff", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", right: -35, top: -35, width: 130, height: 130, borderRadius: "50%", background: C.gold + "16" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: 13, background: C.gold + "22", display: "flex", alignItems: "center", justifyContent: "center" }}><Mail size={18} color={C.gold} /></div>
              <div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,.48)", textTransform: "uppercase", letterSpacing: ".1em" }}>Recipients</div>
                <div style={{ fontSize: 34, fontWeight: 800, color: C.gold, fontFamily: "'Poiret One', cursive", lineHeight: 1 }}>{previewLoading ? "…" : recipientCount}</div>
              </div>
            </div>
            <p style={{ color: "rgba(255,255,255,.55)", fontSize: 12, marginTop: 14 }}>Emails are sent one-by-one through your SMTP so customers do not see each other’s addresses.</p>
          </div>

          <div style={{ background: "#fff", borderRadius: 20, padding: 20, border: "1px solid rgba(31,61,58,0.08)", boxShadow: "0 2px 16px rgba(31,61,58,0.06)" }}>
            <h4 style={{ fontFamily: "'Poiret One', cursive", fontSize: 16, color: C.forest, marginBottom: 12 }}>Email Preview</h4>
            <div style={{ border: "1px solid rgba(31,61,58,0.08)", borderRadius: 16, overflow: "hidden", background: C.cream }}>
              <div style={{ background: C.forest, padding: "18px 18px 14px", color: "#fff" }}>
                <div style={{ fontSize: 11, color: C.gold, letterSpacing: ".12em", textTransform: "uppercase" }}>Dar Oulmes</div>
                <div style={{ fontSize: 19, marginTop: 7, fontWeight: 800 }}>{form.title || form.subject || "Your campaign title"}</div>
                {form.preheader && <p style={{ fontSize: 12, color: "rgba(255,255,255,.6)", marginTop: 5 }}>{form.preheader}</p>}
              </div>
              <div style={{ padding: 18, background: "#fff" }}>
                <p style={{ whiteSpace: "pre-wrap", fontSize: 13, color: C.tan, lineHeight: 1.65 }}>{form.message || "Your campaign message will appear here."}</p>
                {form.ctaLabel && form.ctaUrl && <div style={{ marginTop: 16 }}><span style={{ display: "inline-flex", background: C.gold, color: C.forest, borderRadius: 999, padding: "9px 15px", fontSize: 12, fontWeight: 800 }}>{form.ctaLabel}</span></div>}
              </div>
            </div>
          </div>

          <div style={{ background: "#fff", borderRadius: 20, padding: 20, border: "1px solid rgba(31,61,58,0.08)" }}>
            <h4 style={{ fontFamily: "'Poiret One', cursive", fontSize: 16, color: C.forest, marginBottom: 12 }}>Sample Recipients</h4>
            {preview.sample?.length ? preview.sample.slice(0, 8).map((u, i) => (
              <div key={u.email || i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "8px 0", borderBottom: "1px solid rgba(31,61,58,0.05)", fontSize: 12 }}>
                <span style={{ color: C.forest, overflow: "hidden", textOverflow: "ellipsis" }}>{u.email}</span>
                <Badge variant={u.role === "ADMIN" ? "admin" : u.role === "CUSTOM" ? "gold" : "sand"}>{u.role || "USER"}</Badge>
              </div>
            )) : <p style={{ color: "#aaa", fontSize: 12 }}>No recipients found.</p>}
          </div>
        </div>
      </div>

      {lastResult && (
        <div style={{ background: "#fff", borderRadius: 18, border: `1px solid ${lastResult.failed ? "rgba(239,68,68,.2)" : "rgba(34,197,94,.2)"}`, padding: 18, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div>
            <h4 style={{ color: C.forest, fontSize: 15, fontFamily: "'Poiret One', cursive", marginBottom: 4 }}>Last campaign result</h4>
            <p style={{ color: C.tan, fontSize: 12 }}>{lastResult.sent} sent · {lastResult.failed} failed · {lastResult.total} total</p>
            {!!lastResult.failures?.length && <p style={{ color: "#b91c1c", fontSize: 12, marginTop: 4 }}>First error: {lastResult.failures[0].email} — {lastResult.failures[0].error}</p>}
          </div>
          <Badge variant={lastResult.failed ? "danger" : "success"}>{lastResult.failed ? "Needs review" : "Delivered"}</Badge>
        </div>
      )}
    </div>
  );
}

// ─── TABS + MAIN ──────────────────────────────────────────────────────────────
const TABS = [
  { key:"dashboard",  label:"Dashboard",  icon:LayoutDashboard },
  { key:"users",      label:"Users",      icon:Users           },
  { key:"categories", label:"Categories", icon:Tag             },
  { key:"products",   label:"Products",   icon:Package         },
  { key:"metadata",   label:"Metadata",   icon:Layers          },
  { key:"orders",     label:"Orders",     icon:ShoppingCart    },
  { key:"reviews",    label:"Reviews",    icon:Star            },
  { key:"sessions",   label:"Sessions",   icon:Database        },
  { key:"campaigns",  label:"Campaigns",  icon:Megaphone       },
];

export default function Admin() {
  const [tab, setTab] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);
  const [toastType, setToastType] = useState("success");

  const toast = (msg, type = "success") => { setToastMsg(msg); setToastType(type); setTimeout(()=>setToastMsg(null), 3500); };
  const activeTab = TABS.find(t => t.key === tab);
  useEffect(()=>{ setMobileMenuOpen(false); }, [tab]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poiret+One&display=swap');
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        * { box-sizing:border-box; margin:0; padding:0; }
        ::-webkit-scrollbar { width:5px; height:5px; }
        ::-webkit-scrollbar-track { background:#f3eee9; }
        ::-webkit-scrollbar-thumb { background:#c8a98a; border-radius:99px; }
        @media(max-width:768px){
          .stats-grid { grid-template-columns:1fr 1fr !important; }
          .revenue-orders-grid { grid-template-columns:1fr !important; }
          .categories-grid { grid-template-columns:1fr 1fr !important; }
          .reviews-grid { grid-template-columns:1fr !important; }
          .campaign-grid { grid-template-columns:1fr !important; }
          .section-header { flex-direction:column !important; align-items:stretch !important; }
          header { padding:0 16px !important; }
          main { padding:16px !important; }
        }
        @media(min-width:769px){ .mobile-menu-btn{ display:none !important; } }
        .sidebar-overlay { display:none; position:fixed; inset:0; background:rgba(0,0,0,0.4); z-index:998; }
        @media(max-width:768px){ .sidebar-overlay.show{ display:block; } }
      `}</style>

      <Toast msg={toastMsg} type={toastType}/>

      <div style={{ display:"flex", height:"100vh", fontFamily:"'Poiret One', cursive", paddingTop:"80px", overflow:"hidden" }}>
        <button className="mobile-menu-btn" onClick={()=>setMobileMenuOpen(!mobileMenuOpen)}
          style={{ position:"fixed", top:20, left:16, zIndex:999, background:C.forest, color:C.gold, border:"none", borderRadius:8, padding:"8px 12px", display:"flex", alignItems:"center", gap:8, cursor:"pointer", boxShadow:"0 4px 12px rgba(0,0,0,0.15)" }}>
          <Menu size={18}/><span style={{fontSize:14,fontWeight:600}}>Menu</span>
        </button>

        <div className={`sidebar-overlay ${mobileMenuOpen?"show":""}`} onClick={()=>setMobileMenuOpen(false)}/>

        <aside style={{
          width: sidebarOpen?230:64, transition:"width 0.25s cubic-bezier(.4,0,.2,1)",
          background:C.forest, display:"flex", flexDirection:"column", flexShrink:0, overflow:"hidden", position:"relative", zIndex:999,
          ...(window.innerWidth<=768&&{position:"fixed",left:mobileMenuOpen?0:-230,top:0,bottom:0,height:"100vh",width:230,transition:"left 0.25s ease",boxShadow:mobileMenuOpen?"4px 0 20px rgba(0,0,0,0.2)":"none"}),
        }}>
          <div style={{padding:"24px 18px 20px",borderBottom:"1px solid rgba(255,255,255,0.08)",display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:32,height:32,background:C.gold,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <Activity size={18} color={C.forest}/>
            </div>
            {sidebarOpen&&<span style={{fontFamily:"'Poiret One', cursive",fontSize:18,color:C.gold,whiteSpace:"nowrap"}}>Dar Oulmes</span>}
          </div>
          <nav style={{flex:1,padding:"12px 10px",display:"flex",flexDirection:"column",gap:2,overflowY:"auto"}}>
            {TABS.map(t => {
              const active = tab===t.key;
              const Icon = t.icon;
              return (
                <button key={t.key} onClick={()=>setTab(t.key)} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 10px",borderRadius:12,border:"none",background:active?C.gold+"22":"transparent",color:active?C.gold:"rgba(255,255,255,0.5)",cursor:"pointer",fontFamily:"'Poiret One', cursive",fontWeight:active?700:500,fontSize:13,width:"100%",transition:"all 0.15s",borderLeft:active?`3px solid ${C.gold}`:"3px solid transparent"}}>
                  <Icon size={18} style={{flexShrink:0}}/>
                  {sidebarOpen&&<span style={{whiteSpace:"nowrap"}}>{t.label}</span>}
                </button>
              );
            })}
          </nav>
          <div style={{padding:"12px 10px",borderTop:"1px solid rgba(255,255,255,0.08)"}}>
            <button onClick={()=>setSidebarOpen(o=>!o)} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 10px",borderRadius:12,border:"none",background:"transparent",color:"rgba(255,255,255,0.35)",cursor:"pointer",fontFamily:"'Poiret One', cursive",fontSize:13,width:"100%"}}>
              <ChevronDown size={18} style={{transform:sidebarOpen?"rotate(90deg)":"rotate(-90deg)",transition:"transform 0.2s",flexShrink:0}}/>
              {sidebarOpen&&<span>Collapse</span>}
            </button>
          </div>
        </aside>

        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
          <header style={{background:"#fff",padding:"0 28px",height:60,display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid rgba(31,61,58,0.08)",flexShrink:0}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:4,height:22,background:C.gold,borderRadius:99}}/>
              <h1 style={{fontFamily:"'Poiret One', cursive",fontSize:20,color:C.forest}}>{activeTab?.label}</h1>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{fontSize:12,color:C.tan}}>{new Date().toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short",year:"numeric"})}</div>
              <div style={{width:36,height:36,borderRadius:12,background:C.forest,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <span style={{color:C.gold,fontWeight:800,fontSize:13}}>A</span>
              </div>
            </div>
          </header>
          <main style={{flex:1,overflowY:"auto",padding:"24px 28px",background:"#f8f6f3"}}>
            {tab==="dashboard"  && <Dashboard/>}
            {tab==="users"      && <UsersSection toast={toast}/>}
            {tab==="categories" && <CategoriesSection toast={toast}/>}
            {tab==="products"   && <ProductsSection toast={toast}/>}
            {tab==="metadata"   && <MetadataSection toast={toast}/>}
            {tab==="orders"     && <OrdersSection toast={toast}/>}
            {tab==="reviews"    && <ReviewsSection toast={toast}/>}
            {tab==="sessions"   && <SessionsSection toast={toast}/>}
            {tab==="campaigns"  && <CampaignsSection toast={toast}/>}
          </main>
        </div>
      </div>
    </>
  );
}